// Settings — Company Details · Chart of Accounts · Branding & Comms · Reminders · AI · API Keys.
// (Stock/Products & Services moved to its own top-level screen — see products.js.)
// UI-parity S13: the iT settings LOOK (page-head eyebrow, card + h3+small per topic, grid2/fld
// fields, hints, form-actions) over the wave's own tabbed content — the tabs stay (wave carries
// far more than the sandbox's single page) as an iT segs strip. Company-form enum pickers
// (language, currency, FX pair) are searchSelects per the dropdown-parity rule; CoA / AI / API
// keys keep their native controls (aboo-schema, restyle only).
// Wednesday-core scope (Andre 2026-08-24): VAT codes / currencies / translations / import are
// NOT company settings — master data lives in the admin surface (admin-staging is the
// authority); companies carry flags only. GL codes are flat 4-char (bank GLs share 9500).
import { effect, html, signal } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { companyRole, isCompanyAdmin } from '../shared/companyrole.js';
import { t, locale, reloadI18n } from '../shared/i18n.js';
import { tenantShell } from './tshell.js';
import { paneFx } from '../shared/pane.js';
import { fmtRS, formatGlCode, filterGlAccounts, setBaseCurrencySymbol, setBaseCurrencyCode, setBaseCurrencyId } from './bank_helpers.js';
import { forgetCompanyCurrencies } from '../shared/currencies.js';
import { rowDelete } from '../shared/confirm.js';
import { pager, perPageSelect, DEFAULT_PER_PAGE } from '../shared/pager.js';
import { mountFloatRoot, closeFloatRoot } from '../shared/floatroot.js';
import { dropdownKeydown } from '../shared/dropnav.js';
import { searchSelect } from '../shared/searchselect.js';
import { notice } from '../shared/notice.js';
import { remittanceFields, REMIT_KEYS } from '../shared/remittance.js';
import { configureSleekDate } from '../shared/sleekdate.js';
import '../shared/sleekdate.js';   // registers <sleek-date>

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];
// Tab set mirrors STAGING's settings exactly (owner 2026-09-05, twice: "make the settings like
// it is on staging" — API Keys yes, NO AI tab): Company Details · Chart of Accounts · Branding &
// Comms · API Keys. The per-user Gemini key endpoints stay server-side and bank AI falls back to
// the shared server key, exactly as staging behaves today. The reminder/quote-follow-up settings
// (X2 — the invoiceTest settings' own dunning cards, which staging predates) live INSIDE Branding
// & Comms (they configure automatic emails), not as an extra tab. Staging's remaining tab, Stock,
// lives at /products (owner-approved move, parity Wave B #22 — all four sub-areas ported in S8).
// The Forex tab is added at the TOP of the list when the company has multi-currency
// turned on (owner Andre 2026-09-17: "add forex as its own tab at the top when we
// toggle forex on"). The visibleTabs() helper inside the component reads cMulti and
// splices it in.
const BASE_TABS = [
  ['company', 'Company Details', 'common.company_details'],
  ['coa', 'Chart of Accounts', 'coa.title'],
  ['branding', 'Branding & Comms', 'settings.tab_branding'],
  ['apikeys', 'API Keys & MCP', 'api_keys.tab_mcp'],   // MCP lives here — named so clients find it
];
const FOREX_TAB = ['forex', 'Forex', 'settings.tab_forex'];
const TABS = BASE_TABS;   // legacy: initialSettingsTab treats the base + optional forex

// Stock used to be a tab here — promoted to its own top-level screen at `/co/{id}/products`
// (documents parity sweep Wave B, #22; see products.js + tshell.js's `products` TNAV entry), so
// 'stock' is no longer a recognised TABS key. The `?tab=` reader below stays for the REMAINING
// tabs (e.g. a shared Branding link) — a stale `?tab=stock` bookmark/link now simply falls
// through to Company Details instead of landing on a tab that no longer exists here.
function initialSettingsTab(){
  try {
    const q = new URLSearchParams(location.search).get('tab');
    // Any base tab OR 'forex' (visibleTabs decides whether it renders; a stale ?tab=forex
    // when Forex is off falls back to 'company' the same way ?tab=stock does).
    return (BASE_TABS.some(([k]) => k === q) || q === 'forex') ? q : 'company';
  } catch { return 'company'; }
}
const KEY_ENVS = [['live', 'Live', 'api_keys.env_live'], ['test', 'Test', 'api_keys.env_test']];
const MCP_COMPANY_LEVELS = [['viewer', 'Viewer'], ['capturer', 'Capturer'],
  ['bookkeeper', 'Bookkeeper'], ['accountant', 'Accountant']];
// Exact role vectors returned by api_keys_service for company principals. The list endpoint does
// not return granted_level, so an unfamiliar or incomplete vector must remain visibly Unknown.
const MCP_COMPANY_ROLE_VECTORS = [
  ['viewer', [true, false, false, false]],
  ['capturer', [false, false, false, false]],
  ['bookkeeper', [false, true, false, false]],
  ['accountant', [false, true, true, true]],
];
function companyMcpRoleLabel(key){
  if(key.principal_kind === 'user') return 'User-bound';
  if(key.principal_kind !== 'company') return 'Unknown';
  const fields = ['granted_is_readonly', 'granted_can_post', 'granted_can_govern', 'granted_can_manage_users'];
  if(!fields.every(field => typeof key[field] === 'boolean')) return 'Unknown';
  const vector = fields.map(field => key[field]);
  return (MCP_COMPANY_ROLE_VECTORS.find(([, expected]) => expected.every((v, i) => v === vector[i])) || [])[0] || 'Unknown';
}
// MCP key permissions — the fixed scope catalogue the AI/MCP endpoint enforces (mirrors
// src/app/mcp/core.py SCOPES). Grouped for the checklist: [scope, label-key, default-label].
const MCP_SCOPES = [
  ['reports:read',      'mcp_keys.sc_reports',    'Read reports, ledgers & statements'],
  ['documents:read',    'mcp_keys.sc_docs_read',  'Read documents'],
  ['documents:write',   'mcp_keys.sc_docs_write', 'Create & post documents'],
  ['documents:govern',  'mcp_keys.sc_docs_gov',   'Void / cancel documents (governance)'],
  ['payments:write',    'mcp_keys.sc_pay',        'Record payments'],
  ['banking:read',      'mcp_keys.sc_bank_read',  'Read bank accounts & lines'],
  ['banking:reconcile', 'mcp_keys.sc_bank_rec',   'Allocate & post bank lines (reconcile)'],
  ['contacts:read',     'mcp_keys.sc_con_read',   'Read contacts & items'],
  ['contacts:write',    'mcp_keys.sc_con_write',  'Create & edit contacts & items'],
  ['journals:read',     'mcp_keys.sc_jnl_read',   'Read journals'],
  ['journals:write',    'mcp_keys.sc_jnl_write',  'Create & post journals'],
  ['chart:read',        'mcp_keys.sc_chart_read', 'Read the chart of accounts & tax codes'],
  ['chart:write',       'mcp_keys.sc_chart_write','Map & change the chart of accounts'],
  ['expenses:read',     'mcp_keys.sc_exp_read',   'Read expense claims'],
  ['expenses:write',    'mcp_keys.sc_exp_write',  'Submit expense claims'],
  ['expenses:approve',  'mcp_keys.sc_exp_approve','Approve & reject expense claims'],
  ['company:read',      'mcp_keys.sc_co_read',    'Read company details'],
  ['company:write',     'mcp_keys.sc_co_write',   'Change company details (admin)'],
  ['users:read',        'mcp_keys.sc_users_read', 'See who has access (admin)'],
  ['users:write',       'mcp_keys.sc_users_write','Invite & remove users (admin)'],
];
// The MCP endpoint, on the address the client already uses (the web proxies /api to the API).
export const mcpUrl = () => (typeof location !== 'undefined' ? location.origin : '') + '/api/mcp';
// Ready-to-paste client setup for a freshly created key: Claude Code's command, and the JSON that
// Claude Desktop / Cursor / other MCP apps take (remote HTTP server + bearer header).
export function mcpClientConfig(secret, url = mcpUrl()){
  return {
    cli: `claude mcp add --transport http aboo ${url} --header "Authorization: Bearer ${secret}"`,
    json: JSON.stringify({ mcpServers: { aboo: { type: 'http', url,
      headers: { Authorization: `Bearer ${secret}` } } } }, null, 2),
  };
}
const COMM_KINDS = ['invoice', 'statement', 'invite', 'portal', 'billing', 'reminder', 'custom'];
const fmtDate = s => { if(!s) return ''; try { return new Date(s).toLocaleDateString(); } catch { return s; } };

// `initialTab` (from the path route /co/{id}/settings/{tab}) deep-links a tab in-app — the setup
// guide uses it to land on the RIGHT tab (Branding for the logo step, etc.) rather than the generic
// Company Details. It wins over ?tab= (which only works on a full page load — navigate() drops the
// query, see CLAUDE.md), and falls back to it when absent/unknown.
export function settingsScreen(companyId, initialTab){
  const startTab = (initialTab && (BASE_TABS.some(([k]) => k === initialTab) || initialTab === 'forex'))
    ? initialTab
    : initialSettingsTab();
  const bundle = signal(null), loading = signal(true), err = signal(''), tab = signal(startTab);
  // company form — identity, contact, address, locale, FY start (day+month), VAT, toggles
  const cName = signal(''), cReg = signal(''), cEmail = signal(''), cPhone = signal(''),
        cAddr1 = signal(''), cAddr2 = signal(''), cTown = signal(''), cPostal = signal(''),
        cLang = signal(''), cCur = signal(''),
        cFyDay = signal('1'), cFyMonth = signal('3'), cVatReg = signal(false),
        cVatNo = signal(''), cTaxNo = signal(''), cSub = signal(false), cMulti = signal(false),
        cAutoPost = signal(true), cQuoteDays = signal('30'), cInvoiceDays = signal('30'),
        cApproval = signal(false),   // optional per-company EXPENSE approval gate (migration 140)
        // the accounting standard the company was created with (shown read-only: it never changes) + its country
        cStd = signal({ std: '', country: '' }),
        // Default GL accounts for free-form document lines (migration 131) — sales side / purchase side.
        cDefSales = signal(''), cDefPurch = signal(''),
        // #26 active-currency list: the currency ids this company has enabled (base always in). Feeds
        // the per-bank-account (#24) + per-customer (#25) pickers. The Currencies card toggles this
        // set (immediate PUT) and the company form also re-sends it on Save.
        activeCur = signal(new Set()), curNote = signal(''),
        savingCo = signal(false), coNote = signal('');
  // FX exchange rates (multi-currency only) — reference.exchange_rate, entered manually
  const rates = signal([]), ratesLoaded = signal(false), baseCurId = signal(null),
        rFrom = signal(''), rTo = signal(''), rDate = signal(''), rRate = signal(''),
        rBusy = signal(false), rErr = signal(''), revalBusy = signal(false), revalNote = signal('');
  // support access — a one-time code aboo support redeems (admin/user_detail.js) to view this
  // account for a limited time. No GET exists for an already-active code (API contract), so this
  // card only ever reflects a code generated THIS session — see the build report.
  const supCode = signal(''), supExpiresAt = signal(null), supBusy = signal(false),
        supErr = signal(''), supTick = signal(0);
  // coa
  const search = signal(''), typeF = signal('all'), confirmDel = signal(null), delBusy = signal(false), delErr = signal('');
  const coaPage = signal(1), coaPer = signal(DEFAULT_PER_PAGE);
  let coaSearchTimer;   // debounce the CoA search write (it re-renders coaTab + replaces the input)
  const formOpen = signal(false), editId = signal(null);
  const aCode = signal(''), aName = signal(''), aType = signal(''), aTax = signal(''), aBank = signal(false),
        aContra = signal(''), aBusy = signal(false), aErr = signal('');
  const aTaxOrig = signal('');                        // the VAT code the edit form opened with
  // re-stamp offer after a default-VAT-code change: {accountId, count}
  const restampOffer = signal(null), restampBusy = signal(false);
  const reAcct = signal(''), reBusy = signal(false), reNote = signal('');
  // branding (organisation.branding) + remittance (organisation.company) — lazy-loaded on tab show
  const bLogo = signal(''), bPrimary = signal(''), bSecondary = signal(''), bTerms = signal(''),
        bPayTerms = signal(''), bFooter = signal(''), bBankName = signal(''), bAcctNo = signal(''),
        bAcctName = signal(''), bBranch = signal(''), bLoaded = signal(false), bBusy = signal(false), bNote = signal('');
  // Feature 1.3 — international remittance (SWIFT/BIC, IBAN, US routing, UK sort code) + the
  // company's country (drives which fields show). Feature 1.2 — a logo-upload error line.
  const bSwift = signal(''), bIban = signal(''), bRouting = signal(''), bSort = signal(''),
        bCountry = signal(''), logoErr = signal('');
  // canonical remittance column -> its input signal (keeps settings, the API and the print helper aligned)
  const remitSignals = { bank_name: bBankName, bank_account_name: bAcctName, bank_account_no: bAcctNo,
    bank_branch_code: bBranch, bank_routing_number: bRouting, bank_sort_code: bSort,
    bank_swift: bSwift, bank_iban: bIban };
  // api keys (D8) — list + new-key form + one-time secret + inline revoke
  const keyList = signal([]), keysLoaded = signal(false), kName = signal(''), kEnv = signal('live'),
        kScopes = signal(''), kBusy = signal(false), kErr = signal(''), newSecret = signal(''),
        revokeId = signal(null), revokeBusy = signal(false);
  // MCP-key form (a separate credential kind — see MCP_SCOPES + createMcpKey)
  const mName = signal(''), mEnv = signal('live'), mScopes = signal([]), mBusy = signal(false),
        mErr = signal(''), mSecret = signal('');
  // Company-scoped MCP keys use the admin-only management API. The existing user-MCP form above
  // deliberately stays on its legacy endpoint and keeps its existing identity semantics.
  const actorCompanyRole = companyRole(companyId);
  const cmKeys = signal([]), cmKeysLoaded = signal(false), cmKeysLoading = signal(false), cmKeysErr = signal(''),
        cmName = signal(''), cmEnv = signal('live'), cmLevel = signal('viewer'),
        cmScopes = signal([]), cmKeepsSourceDates = signal(false), cmBusy = signal(false),
        cmSecret = signal(''), cmErr = signal('');
  const cmRevokeId = signal(null), cmRevokeBusy = signal(false);
  let cmKeysRequest = null;
  // AI key — lives INSIDE the API Keys tab, exactly as on staging. Multi-provider (Gemini / OpenAI /
  // Anthropic / xAI Grok) over GET/PUT/POST-test /companies/{id}/ai-settings (bank_ai_service). Key
  // is write-only (masked on read; a blank input keeps the stored key). server_key = shared env
  // fallback. (Owner 2026-09-06: the transplant had dropped this card — restored to match staging.)
  const aiLoaded = signal(false), aiHasKey = signal(false), aiMasked = signal(''),
        aiKeyIn = signal(''), aiShowKey = signal(false), aiProvider = signal('gemini'),
        aiProviders = signal({}), aiModel = signal(''), aiDefaultModel = signal(''),
        aiPdfPw = signal(''), aiThreshPct = signal(90), aiServerKey = signal(false),
        aiBusy = signal(false), aiTesting = signal(false), aiNote = signal(''), aiErr = signal(''),
        aiDisabled = signal(false), aiEditKey = signal(false);
  const aiProv = () => (aiProviders.value || {})[aiProvider.value] || {};
  // #20 PayFast — the company's OWN online-payment account. merchant_key + passphrase are write-only
  // (blank keeps the stored secret); the read surface never returns their values.
  const pfLoaded = signal(false), pfConfigured = signal(false), pfMerchantId = signal(''),
        pfKeyIn = signal(''), pfPassIn = signal(''), pfHasKey = signal(false), pfHasPass = signal(false),
        pfSandbox = signal(true), pfBusy = signal(false), pfNote = signal(''), pfErr = signal('');
  function aiPickProvider(p){                 // switch provider → default its model + clear the typed key
    aiProvider.value = p;
    const cat = (aiProviders.value || {})[p] || {};
    aiModel.value = cat.default || ((cat.models && cat.models[0] && cat.models[0][0]) || '');
    aiKeyIn.value = ''; aiShowKey.value = false; aiNote.value = ''; aiErr.value = '';
  }
  // communications (E1a) — per-company email templates + read-only outbox log
  const commLoaded = signal(false), commSub = signal('templates'), templates = signal([]), messages = signal([]);
  const tOpen = signal(false), tEditId = signal(null), tCode = signal(''), tName = signal(''),
        tKind = signal('invoice'), tSubject = signal(''), tBody = signal(''), tActive = signal(true),
        tBusy = signal(false), tErr = signal('');
  const tDelId = signal(null), tDelBusy = signal(false), tDelErr = signal('');
  // reminders (documents parity X2) — company-wide automatic overdue-reminder settings
  // (documents.reminder_settings) + an on-demand "send due reminders now" (run-due). enabled +
  // offsets are days-overdue tiers. remOff/remQuoteOff hold the tiers as an int[] (1–365), edited
  // through the day-tier chip UI (removable pills + a number "Add"); remAdd* are the add-box values.
  const remLoaded = signal(false), remEnabled = signal(false), remOff = signal([]),
        remBusy = signal(false), remNote = signal(''), remErr = signal(''),
        remRunning = signal(false), remRun = signal(null), remAddOff = signal(''), remAddQuote = signal(''),
        // quote follow-up ladder (X2) — nudge un-responded issued quotes N days SINCE ISSUE
        remQuoteEnabled = signal(false), remQuoteOff = signal([]);
  // ── iT searchSelects for the company-form enum pickers (S13 dropdown parity). Created ONCE per
  // screen and MOVED into each freshly-rendered tab fragment (companyTab re-renders on tab
  // switches / bundle reloads), so typing in them never fights a re-render. Options land after
  // load(); the pickers write plain signals the view slots never read — no focus trap.
  const langSel = searchSelect({ options: [], value: '',
    placeholder: t('settings.select_language', '— Select a language —'),
    ariaLabel: t('common.language', 'Language'), onSelect: v => { cLang.value = v; } });
  const curSel = searchSelect({ options: [], value: '',
    placeholder: t('settings.select_currency', '— Select a currency —'),
    ariaLabel: t('settings.default_currency', 'Default currency'), onSelect: v => { cCur.value = v; } });
  // Focus-safe stale-banner clear for the FX add-rate form: the error is a .notice span read at the
  // top of companyTab(), so a signal write here would re-run that whole view and rebuild the very
  // input being typed into (the tina4 focus-trap) — instead we clear the .notice text imperatively.
  // addRate() resets rErr to '' before re-validating, so a recurring identical error still re-shows.
  const clearFxNotice = () => { const n = document.querySelector('.fx-card .notice'); if(n) n.textContent = ''; };
  const fxFromSel = searchSelect({ options: [], value: '',
    placeholder: t('settings.select_currency', '— Select a currency —'),
    ariaLabel: t('settings.from_currency', 'From currency'), onSelect: v => { rFrom.value = v; clearFxNotice(); } });
  const fxToSel = searchSelect({ options: [], value: '',
    placeholder: t('settings.select_currency', '— Select a currency —'),
    ariaLabel: t('settings.to_currency', 'To currency (base)'), onSelect: v => { rTo.value = v; clearFxNotice(); } });
  // ── CoA account-form searchSelects (owner 2026-09-17: type + tax must search like every other
  // dropdown; contra is NEW — it feeds the journals auto-contra). Created once, .el moved into the
  // freshly-built accForm fragment each render (same stable-node pattern as the company pickers).
  const typeSel = searchSelect({ options: [], value: '',
    placeholder: t('settings.select_type', '— Select a type —'),
    ariaLabel: t('settings.account_type', 'Account type'), onSelect: v => { aType.value = v; } });
  const taxSel = searchSelect({ options: [], value: '',
    placeholder: t('settings.tax_none', '— None —'),
    ariaLabel: t('settings.default_tax_code', 'Default tax code'), onSelect: v => { aTax.value = v; } });
  const contraSel = searchSelect({ options: [], value: '',
    placeholder: t('settings.contra_none', '— None (no auto-contra) —'),
    ariaLabel: t('settings.contra_account', 'Contra account'), onSelect: v => { aContra.value = v; } });
  // Company default GL accounts for free-form document lines (Company Details → Documents). Same
  // stable-node pattern: created once, .el moved into the freshly-built company fragment each render.
  const defSalesSel = searchSelect({ options: [], value: '',
    placeholder: t('settings.default_none', '— None —'),
    ariaLabel: t('settings.default_sales_account', 'Default sales account'), onSelect: v => { cDefSales.value = v; } });
  const defPurchSel = searchSelect({ options: [], value: '',
    placeholder: t('settings.default_none', '— None —'),
    ariaLabel: t('settings.default_purchase_account', 'Default purchase account'), onSelect: v => { cDefPurch.value = v; } });

  // ── the app-wide imperative float dropdown (documents/journals convention) —
  // used by the year-end (retained earnings) account typeahead
  const floatRoot = mountFloatRoot();   // shared layer — ONE document listener app-wide
  const closeFloat = closeFloatRoot;
  let dropEl = null;
  function positionNear(el, anchor, width){
    const r = anchor.getBoundingClientRect();
    el.style.position = 'fixed';
    el.style.left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8)) + 'px';
    el.style.zIndex = 300;
    floatRoot.appendChild(el);
    const h = el.offsetHeight, below = window.innerHeight - r.bottom - 8;
    el.style.top = (below >= h ? r.bottom + 4 : Math.max(8, r.top - h - 4)) + 'px';
  }
  function showReDrop(anchor){
    const bs = accounts().filter(a => a.statement === 'BS')
      .map(a => ({ ...a, code: gcode(a.code) }));
    const rows = filterGlAccounts(bs, anchor.value);
    closeFloat();
    if(!anchor.value.trim()) return;
    dropEl = document.createElement('div');
    dropEl.className = 'cb-dropdown';
    if(!rows.length) dropEl.innerHTML = `<div class="cb-drop-item cb-drop-muted">${t('bank_import.no_matches', 'No matches')}</div>`;
    rows.forEach(a => {
      const item = document.createElement('div');
      item.className = 'cb-drop-item';
      item.innerHTML = `<span><span class="mono">${a.code}</span> ${a.name}</span>`;
      item.onclick = ev => { ev.stopPropagation(); reAcct.value = String(a.id);
        anchor.value = a.code + ' — ' + a.name; closeFloat(); };
      dropEl.appendChild(item);
    });
    positionNear(dropEl, anchor, 350);
  }
  function reKeydown(ev){
    if(floatRoot.childElementCount && dropdownKeydown(ev, dropEl, { wrap: false, onEscape: closeFloat })) return;
  }

  function populate(b){
    const c = b.company || {};
    cName.value = c.name || ''; cReg.value = c.registration_no || '';
    cEmail.value = c.email || ''; cPhone.value = c.phone || '';
    cAddr1.value = c.address_line1 || ''; cAddr2.value = c.address_line2 || '';
    cTown.value = c.address_town || ''; cPostal.value = c.address_postal || '';
    cLang.value = c.language_id ? String(c.language_id) : '';
    cCur.value = c.base_currency_id ? String(c.base_currency_id) : '';
    baseCurId.value = c.base_currency_id || null;
    cFyDay.value = String(c.fy_start_day || 1); cFyMonth.value = String(c.fy_start_month || 3);
    cStd.value = { std: c.accounting_standard || '', country: c.country_name || '' };
    cVatReg.value = !!c.vat_registered;
    cVatNo.value = c.vat_no || ''; cTaxNo.value = c.tax_no || '';
    cSub.value = !!c.subcodes_enabled; cMulti.value = !!c.multi_currency_enabled;
    activeCur.value = new Set((b.active_currency_ids || []).map(Number));
    cAutoPost.value = c.auto_post_imports !== false;   // default on when the field is absent
    cApproval.value = !!c.approval_required;            // expense approval gate (default off)
    cQuoteDays.value = String(c.quote_valid_days ?? 30); cInvoiceDays.value = String(c.invoice_due_days ?? 30);
    cDefSales.value = c.default_sales_account_id ? String(c.default_sales_account_id) : '';
    cDefPurch.value = c.default_purchase_account_id ? String(c.default_purchase_account_id) : '';
    const re = (b.accounts || []).find(a => a.is_retained_earnings);
    reAcct.value = re ? String(re.id) : '';
    // postable, non-bank accounts feed the two default pickers (a "— None —" clear is the blank value)
    const acctOpts = [{ value: '', label: t('settings.default_none', '— None —') },
      ...(b.accounts || []).filter(a => a.is_postable !== false && !a.is_bank)
        .map(a => ({ value: String(a.id), label: gcode(a.code) + ' — ' + a.name, keywords: a.name }))];
    defSalesSel.setOptions(acctOpts); defSalesSel.set(cDefSales.value);
    defPurchSel.setOptions(acctOpts); defPurchSel.set(cDefPurch.value);
    // feed the iT pickers: options from the bundle, values from the company row
    const langOpts = (b.languages || []).map(l => ({ value: String(l.id), label: l.name }));
    const curOpts = (b.currencies || []).map(u => ({ value: String(u.id), label: u.code + ' · ' + u.name, keywords: u.code }));
    langSel.setOptions(langOpts); langSel.set(cLang.value);
    curSel.setOptions(curOpts); curSel.set(cCur.value);
    fxFromSel.setOptions(curOpts); fxFromSel.set(rFrom.value);
    fxToSel.setOptions(curOpts); fxToSel.set(rTo.value);
  }
  async function load(){
    loading.value = true;
    try {
      const b = await api.get('/companies/' + companyId + '/settings'); bundle.value = b; populate(b); err.value = '';
      const c = b.company || {};
      configureSleekDate({ date_format: c.effective_date_format || c.date_format,
        fy_start_day: c.fy_start_day, fy_start_month: c.fy_start_month });
    }
    catch(e){ err.value = (e.data && e.data.error) || t('settings.err_load', 'Could not load settings'); }
    loading.value = false;
    if(cMulti.value) loadRates();
  }
  load();

  async function loadRates(){
    try {
      const r = await api.get('/companies/' + companyId + '/settings/exchange-rates');
      rates.value = r.rates || [];
      if(r.base_currency_id) baseCurId.value = r.base_currency_id;
      if(!rTo.value && baseCurId.value){ rTo.value = String(baseCurId.value); fxToSel.set(rTo.value); }
    } catch { rates.value = []; }
    ratesLoaded.value = true;
  }
  // ── Currencies card (v2-parity Settings table) ──────────────────────────────────────────────
  // Rate vs base for one currency, read from the already-loaded manual rates (list_rates): the
  // latest DIRECT rate (currency → base) wins, else the latest INVERSE (base → currency, 1/rate),
  // else null → "—". Display only — nothing converts on the ledger until the FX pipeline lands.
  function rateVsBase(code, baseCode, rows){
    if(!baseCode || !code || code === baseCode) return null;
    const byDate = (a, b) => String(b.rate_date).localeCompare(String(a.rate_date));
    const direct = (rows || []).filter(r => r.from_code === code && r.to_code === baseCode).sort(byDate)[0];
    if(direct) return Number(direct.rate);
    const inv = (rows || []).filter(r => r.from_code === baseCode && r.to_code === code && Number(r.rate)).sort(byDate)[0];
    if(inv) return 1 / Number(inv.rate);
    return null;
  }
  function fmtRate(r){ return (r == null) ? '—' : String(+Number(r).toFixed(6)); }
  async function persistCurrencies(){
    curNote.value = '';
    try {
      await api.put('/companies/' + companyId + '/settings/active-currencies',
        { currency_ids: [...activeCur.value] });
      curNote.value = ''; notice(t('settings.cur_saved', 'Saved'));
    } catch { curNote.value = t('settings.cur_save_fail', 'Could not save — please try again'); }
  }
  function toggleCur(id){
    const s = new Set(activeCur.value);
    if(s.has(id)) s.delete(id); else s.add(id);
    activeCur.value = s;                 // reassign (not mutate) → the Currencies pills re-render
    persistCurrencies();                 // immediate save; the company form also re-sends the set
  }
  async function addRate(e){
    e.preventDefault();
    rErr.value = '';
    if(!rFrom.value || !rTo.value || !rDate.value || !rRate.value){ rErr.value = t('settings.fx_all_required', 'All fields are required.'); return; }
    rBusy.value = true; rErr.value = '';
    try {
      await api.post('/companies/' + companyId + '/settings/exchange-rates', {
        from_currency_id: Number(rFrom.value), to_currency_id: Number(rTo.value),
        rate_date: rDate.value, rate: Number(rRate.value) });
      rFrom.value = ''; rRate.value = '';   // keep rTo (base) + date for quick repeat entry
      fxFromSel.set('');
      await loadRates();
    } catch(e2){ rErr.value = (e2.data && e2.data.error) || t('settings.fx_save_failed', 'Could not save the rate'); }
    rBusy.value = false;
  }
  async function doRevalue(){
    revalBusy.value = true; revalNote.value = '';
    try {
      const r = await api.post('/companies/' + companyId + '/settings/revalue', {});
      notice(r.revalued
        ? (r.revalued + ' account' + (r.revalued === 1 ? '' : 's') + ' revalued')
        : (r.message || t('settings.fx_nothing_revalue', 'Nothing to revalue')));
    } catch(e3){ revalNote.value = (e3.data && e3.data.error) || t('settings.fx_revalue_failed', 'Revaluation failed'); }
    revalBusy.value = false;
  }

  // support access — countdown ticks every second while a code is showing (cleared the instant
  // it's revoked or its own expiry fires), same auto-clear-via-timer idiom as
  // shared/impersonate.js's scheduleAutoStop, so the code never lingers on screen past its expiry.
  let supTickTimer = null, supClearTimer = null;
  function stopSupTick(){ if(supTickTimer){ clearInterval(supTickTimer); supTickTimer = null; } }
  function scheduleSupClear(expiresAt){
    clearTimeout(supClearTimer);
    const ms = expiresAt ? new Date(expiresAt).getTime() - Date.now() : -1;
    if(ms > 0) supClearTimer = setTimeout(() => { supCode.value = ''; supExpiresAt.value = null; stopSupTick(); }, ms);
  }
  function fmtCountdown(expiresAt){
    const secs = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
    return String(Math.floor(secs / 60)).padStart(2, '0') + ':' + String(secs % 60).padStart(2, '0');
  }
  async function generateSupportCode(){
    supBusy.value = true; supErr.value = '';
    try {
      const r = await api.post('/me/support-code', {});
      supCode.value = r.code; supExpiresAt.value = r.expires_at;
      clearInterval(supTickTimer); supTickTimer = setInterval(() => { supTick.value++; }, 1000);
      scheduleSupClear(r.expires_at);
    } catch(e4){ supErr.value = (e4.data && e4.data.error) || t('support.generate_failed', 'Could not generate a code'); }
    supBusy.value = false;
  }
  async function revokeSupportCode(){
    supBusy.value = true; supErr.value = '';
    try {
      await api.del('/me/support-code');
      supCode.value = ''; supExpiresAt.value = null; stopSupTick(); clearTimeout(supClearTimer);
    } catch(e5){ supErr.value = (e5.data && e5.data.error) || t('support.revoke_failed', 'Could not revoke the code'); }
    supBusy.value = false;
  }

  const gcode = c => formatGlCode(c);
  const pane = paneFx();   // tab/sub-screen switch effect
  // Shared with the initial-mount deep-link below (?tab=…) so a tab reached either way (a click,
  // or landing straight on it via a link) gets its lazy data loaded exactly once, the same way.
  function loadTabData(k){
    if(k === 'branding'){ loadBranding(); loadComm(); loadReminders(); }
    if(k === 'apikeys'){ loadKeys(); loadAI(); loadPayfast(); }
  }
  function selectTab(k){ if(tab.value === k) return;
    tab.value = k; pane(); loadTabData(k); }
  if(tab.value !== 'company') loadTabData(tab.value);   // deep-linked via ?tab= — company/coa data already loads unconditionally below (load())

  const accounts = () => (bundle.value && bundle.value.accounts) || [];
  const types = () => (bundle.value && bundle.value.account_types) || [];
  const vats = () => (bundle.value && bundle.value.tax_codes) || [];
  // option lists for the account-form searchSelects (built from the loaded bundle)
  const typeOpts = () => types().map(ty => ({ value: String(ty.id),
    label: ty.root_code.charAt(0) + ty.root_code.slice(1).toLowerCase() + ' · ' + ty.name }));
  const taxOpts = () => [{ value: '', label: t('settings.tax_none', '— None —') },
    ...vats().map(v => ({ value: String(v.id), label: v.code + ' · ' + v.name }))];
  // contra candidates: any POSTABLE GL bar the account being edited (a control account can't be a
  // free contra, and an account can't contra itself)
  const contraOpts = selfId => [{ value: '', label: t('settings.contra_none', '— None (no auto-contra) —') },
    ...accounts().filter(a => a.is_postable !== false && !a.is_control && String(a.id) !== String(selfId))
      .map(a => ({ value: String(a.id), label: gcode(a.code) + ' — ' + a.name }))];
  function toast(sig, m){ sig.value = m; setTimeout(() => { if(sig.value === m) sig.value = ''; }, 3000); }

  // the app-wide base currency (mark, code, id) and its per-company session cache — see tshell.js
  function adoptBaseCurrency(id){
    const cur = ((bundle.value || {}).currencies || []).find(c => String(c.id) === String(id)) || {};
    setBaseCurrencySymbol(cur.symbol || ''); setBaseCurrencyCode(cur.code || ''); setBaseCurrencyId(id || '');
    try {
      const put = (k, v) => { if(v) sessionStorage.setItem(k + companyId, String(v)); else sessionStorage.removeItem(k + companyId); };
      put('sleek_co_cursym_', cur.symbol); put('sleek_co_curcode_', cur.code); put('sleek_co_curid_', id);
    } catch { /* storage off */ }
    forgetCompanyCurrencies(companyId);
  }
  async function saveCompany(e){
    e.preventDefault(); savingCo.value = true; coNote.value = '';
    if(cVatReg.value && !cVatNo.value.trim()){
      coNote.value = t('settings.vat_no_required', 'Please enter a VAT number — it is required when VAT registered is on.');
      savingCo.value = false; return;
    }
    const before = (bundle.value && bundle.value.company) || {};
    try {
      await api.put('/companies/' + companyId + '/settings/company', {
        name: cName.value, registration_no: cReg.value.trim(), email: cEmail.value.trim(),
        phone: cPhone.value.trim(), address_line1: cAddr1.value.trim(),
        address_line2: cAddr2.value.trim(), address_town: cTown.value.trim(),
        address_postal: cPostal.value.trim(),
        language_id: cLang.value ? Number(cLang.value) : null,
        base_currency_id: cCur.value ? Number(cCur.value) : null,
        // date_format is NOT sent — it always resolves from the company's country
        // (admin Countries). Letting a company pick its own risked breaking dates.
        fy_start_day: Number(cFyDay.value), fy_start_month: Number(cFyMonth.value),
        vat_registered: cVatReg.value, vat_no: cVatNo.value.trim(), tax_no: cTaxNo.value.trim(),
        subcodes_enabled: cSub.value, multi_currency_enabled: cMulti.value,
        auto_post_imports: cAutoPost.value,
        approval_required: cApproval.value,
        quote_valid_days: Number(cQuoteDays.value) || 0, invoice_due_days: Number(cInvoiceDays.value) || 0,
        // '' clears the default; a value is validated server-side (postable, non-bank, owned account)
        default_sales_account_id: cDefSales.value || '', default_purchase_account_id: cDefPurch.value || '' });
      // #26: persist the enabled-currency set (base is always kept by the server). Sent AFTER the
      // company PUT so a base change in the same save is already in effect when the base is re-added.
      await api.put('/companies/' + companyId + '/settings/active-currencies',
        { currency_ids: [...activeCur.value] });
      // the app language is tied to the COMPANY. Labels are keyed via t() but read the bundle at
      // render time (not reactively), so reload the whole app to re-render every screen in the new
      // language once the new bundle is live.
      if(String(before.language_id || '') !== cLang.value){
        await reloadI18n(companyId);
        location.reload();
        return;                      // reload re-renders everything — nothing more to do here
      }
      // A new base currency: every base figure on every screen takes its mark from here on, not the
      // old one cached for this session (owner 2026-10-19: no currency bleeds into another).
      if(String(before.base_currency_id || '') !== cCur.value) adoptBaseCurrency(cCur.value);
      await load(); coNote.value = ''; notice(t('common.saved', 'Saved'));
    } catch(e){ coNote.value = (e.data && e.data.error) || t('common.save_failed', 'Could not save'); }
    savingCo.value = false;
  }

  function openNew(){ editId.value = null; aCode.value = ''; aName.value = ''; aType.value = '';
    aTax.value = ''; aContra.value = ''; aBank.value = false; aErr.value = '';
    typeSel.setOptions(typeOpts()); typeSel.set('');
    taxSel.setOptions(taxOpts()); taxSel.set('');
    contraSel.setOptions(contraOpts(null)); contraSel.set('');
    formOpen.value = true; }
  function openEdit(a){ editId.value = a.id; aCode.value = a.code; aName.value = a.name;
    aType.value = ''; aTax.value = a.default_tax_code_id ? String(a.default_tax_code_id) : '';
    aTaxOrig.value = aTax.value;                       // remember for the re-stamp offer on save
    aContra.value = a.contra_account_id ? String(a.contra_account_id) : '';
    aBank.value = !!a.is_bank; aErr.value = '';
    taxSel.setOptions(taxOpts()); taxSel.set(aTax.value);
    contraSel.setOptions(contraOpts(a.id)); contraSel.set(aContra.value);
    formOpen.value = true; }
  function closeForm(){ formOpen.value = false; editId.value = null; aErr.value = ''; }

  async function saveAccount(e){
    e.preventDefault(); aBusy.value = true; aErr.value = '';
    try {
      if(editId.value){
        const acctId = editId.value;
        const taxChanged = Number(aTax.value || 0) !== Number(aTaxOrig.value || 0);
        const res = await api.put('/companies/' + companyId + '/settings/accounts/' + acctId,
          { name: aName.value, tax_code_id: aTax.value ? Number(aTax.value) : null, is_bank: aBank.value,
            contra_account_id: aContra.value ? Number(aContra.value) : null });
        // VAT safeguard #1: if the default VAT code changed and unposted bank lines target this
        // account, offer to re-stamp them with the new code (not retroactive on its own).
        if(taxChanged && res && res.restampable > 0){
          restampOffer.value = { accountId: acctId, count: res.restampable };
        }
      } else {
        await api.post('/companies/' + companyId + '/settings/accounts',
          { code: aCode.value, name: aName.value, category_id: aType.value ? Number(aType.value) : null,
            tax_code_id: aTax.value ? Number(aTax.value) : null, is_bank: aBank.value,
            contra_account_id: aContra.value ? Number(aContra.value) : null });
      }
      closeForm(); await load();
    } catch(e){ aErr.value = (e.data && e.data.error) || t('settings.account_save_failed', 'Could not save the account'); }
    aBusy.value = false;
  }

  async function doDelete(id){
    delBusy.value = true; delErr.value = '';
    try { await api.post('/companies/' + companyId + '/settings/accounts/' + id + '/delete', {}); confirmDel.value = null; await load(); }
    catch(e){ delErr.value = (e.data && e.data.error) || t('common.delete_failed', 'Delete failed'); }
    delBusy.value = false;
  }

  async function doRestamp(){
    const off = restampOffer.value; if(!off) return;
    restampBusy.value = true;
    try { await api.post('/companies/' + companyId + '/settings/accounts/' + off.accountId + '/restamp-vat', {});
          restampOffer.value = null; }
    catch(e){ /* leave the offer up on failure so the user can retry */ }
    restampBusy.value = false;
  }

  async function saveRetained(){
    if(!reAcct.value) return; reBusy.value = true;
    try { await api.post('/companies/' + companyId + '/settings/retained-earnings', { account_id: Number(reAcct.value) });
      await load(); reNote.value = ''; notice(t('settings.year_end_saved', 'Year-end account saved')); }
    catch(e){ reNote.value = (e.data && e.data.error) || t('common.save_failed', 'Could not save'); }
    reBusy.value = false;
  }

  async function loadBranding(){
    if(bLoaded.value) return;
    try {
      const r = await api.get('/companies/' + companyId + '/settings/branding');
      const b = r.branding || {}, m = r.remittance || {};
      bLogo.value = b.logo_url || ''; bPrimary.value = b.primary_color || ''; bSecondary.value = b.secondary_color || '';
      bTerms.value = b.terms_text || ''; bPayTerms.value = b.payment_terms_text || ''; bFooter.value = b.document_footer || '';
      bBankName.value = m.bank_name || ''; bAcctNo.value = m.bank_account_no || '';
      bAcctName.value = m.bank_account_name || ''; bBranch.value = m.bank_branch_code || '';
      bSwift.value = m.bank_swift || ''; bIban.value = m.bank_iban || '';
      bRouting.value = m.bank_routing_number || ''; bSort.value = m.bank_sort_code || '';
      bCountry.value = r.country_iso2 || '';
      bLoaded.value = true;
    } catch(e){ bNote.value = (e.data && e.data.error) || t('settings.branding_load_failed', 'Could not load branding'); }
  }
  async function saveBranding(e){
    e.preventDefault(); bBusy.value = true; bNote.value = '';
    // remittance = every canonical field from its signal (blank ones save empty — harmless)
    const remittance = {};
    for(const k of REMIT_KEYS) remittance[k] = (remitSignals[k].value || '').trim();
    try {
      await api.put('/companies/' + companyId + '/settings/branding', {
        branding: { logo_url: bLogo.value.trim(), primary_color: bPrimary.value.trim(),
          secondary_color: bSecondary.value.trim(), terms_text: bTerms.value, payment_terms_text: bPayTerms.value,
          document_footer: bFooter.value },
        remittance });
      notice(t('common.saved', 'Saved'));
    } catch(e){ bNote.value = (e.data && e.data.error) || t('common.save_failed', 'Could not save'); }
    bBusy.value = false;
  }
  // Feature 1.2 — logo upload. Read the chosen image, downscale a raster via canvas so the stored
  // data-URI stays small (SVG is kept as-is), then set bLogo to the data-URI. logo_url holds the
  // data-URI (a valid src for the <img> preview and every document surface).
  const LOGO_MAX_PX = 480;
  function loadLogoFile(file){                  // the ONE logo path — fed by both drag-drop and the picker
    if(!file) return;
    logoErr.value = '';
    if(!/^image\//.test(file.type || '')){
      logoErr.value = t('settings.logo_not_image', 'Please choose an image file (PNG, JPG or SVG).'); return; }
    const fr = new FileReader();
    fr.onerror = () => { logoErr.value = t('settings.logo_read_failed', 'Could not read that image.'); };
    fr.onload = () => {
      const src = String(fr.result || '');
      if((file.type || '').indexOf('svg') >= 0){ bLogo.value = src; return; }   // vector — keep as-is
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, LOGO_MAX_PX / Math.max(img.width || 1, img.height || 1));
        const w = Math.max(1, Math.round((img.width || 1) * scale)), h = Math.max(1, Math.round((img.height || 1) * scale));
        const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
        cv.getContext('2d').drawImage(img, 0, 0, w, h);
        try { bLogo.value = cv.toDataURL(file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png', 0.9); }
        catch { bLogo.value = src; }
      };
      img.onerror = () => { logoErr.value = t('settings.logo_read_failed', 'Could not read that image.'); };
      img.src = src;
    };
    fr.readAsDataURL(file);
  }
  function onLogoFile(e){
    const file = (e.target.files || [])[0];
    e.target.value = '';                       // let the same file be chosen again later
    loadLogoFile(file);
  }
  // drag-drop (owner: users drag a logo onto the dropzone). Plain DOM classList for the drag-over
  // state — never a signal — so a dragenter/leave storm can't re-render the whole tab mid-drag.
  function onLogoDragOver(e){ e.preventDefault(); e.currentTarget.classList.add('over'); }
  function onLogoDragLeave(e){ e.currentTarget.classList.remove('over'); }
  function onLogoDrop(e){
    e.preventDefault(); e.stopPropagation();
    e.currentTarget.classList.remove('over');
    const file = ((e.dataTransfer && e.dataTransfer.files) || [])[0];
    loadLogoFile(file);
  }
  function clearLogo(){ bLogo.value = ''; logoErr.value = ''; }

  async function loadKeys(){
    if(keysLoaded.value) return;
    try { const r = await api.get('/companies/' + companyId + '/settings/api-keys'); keyList.value = r.keys || []; keysLoaded.value = true; }
    catch(e){ toast(kErr, (e.data && e.data.error) || t('settings.keys_load_failed', 'Could not load keys')); }
  }
  async function createKey(e){
    e.preventDefault(); kErr.value = ''; newSecret.value = '';
    if(!kName.value.trim()){ kErr.value = t('settings.key_name_required', 'Please give the key a name'); return; }
    kBusy.value = true;
    try {
      const scopes = kScopes.value.split(',').map(s => s.trim()).filter(Boolean);
      const r = await api.post('/companies/' + companyId + '/settings/api-keys',
        { name: kName.value.trim(), environment: kEnv.value, scopes });
      newSecret.value = r.secret || '';                 // shown ONCE — never retrievable again
      kName.value = ''; kScopes.value = '';
      keysLoaded.value = false; await loadKeys();        // refresh the list
    } catch(e){ kErr.value = (e.data && e.data.error) || t('settings.key_create_failed', 'Could not create the key'); }
    kBusy.value = false;
  }
  async function revokeKey(id){
    revokeBusy.value = true;
    try { await api.post('/companies/' + companyId + '/settings/api-keys/' + id + '/revoke', {});
      revokeId.value = null; keysLoaded.value = false; await loadKeys(); }
    catch(e){ toast(kErr, (e.data && e.data.error) || t('settings.revoke_failed', 'Revoke failed')); }
    revokeBusy.value = false;
  }
  function copySecret(){ if(newSecret.value && navigator.clipboard) navigator.clipboard.writeText(newSecret.value); }

  // ── MCP keys (kind='mcp') — same endpoint, a fixed scope checklist, and a separate list ──
  function toggleMcpScope(s){
    const cur = mScopes.value.slice();
    const i = cur.indexOf(s);
    if(i >= 0) cur.splice(i, 1); else cur.push(s);
    mScopes.value = cur;
  }
  async function createMcpKey(e){
    e.preventDefault(); mErr.value = ''; mSecret.value = '';
    if(!mName.value.trim()){ mErr.value = t('mcp_keys.name_required', 'Please give the key a name'); return; }
    if(!mScopes.value.length){ mErr.value = t('mcp_keys.pick_scope', 'Choose at least one permission'); return; }
    mBusy.value = true;
    try {
      const r = await api.post('/companies/' + companyId + '/settings/api-keys',
        { name: mName.value.trim(), environment: mEnv.value, scopes: mScopes.value, kind: 'mcp' });
      mSecret.value = r.secret || '';                    // shown ONCE — never retrievable again
      mName.value = ''; mScopes.value = [];
      keysLoaded.value = false; await loadKeys();          // refresh the list
    } catch(e){ mErr.value = (e.data && e.data.error) || t('mcp_keys.create_failed', 'Could not create the key'); }
    mBusy.value = false;
  }
  function loadCompanyMcpKeys(force = false){
    if(!isCompanyAdmin(actorCompanyRole.value) || (!force && cmKeysLoaded.value)) return Promise.resolve();
    if(cmKeysRequest) return cmKeysRequest;
    cmKeysErr.value = ''; cmKeysLoading.value = true;
    const request = api.get('/companies/' + companyId + '/settings/mcp-keys').then(r => {
      // The endpoint is company-scoped and returns every MCP principal with masked metadata only.
      cmKeys.value = r.keys || [];
      cmKeysLoaded.value = true;
    }).catch(e => {
      cmKeysErr.value = (e.data && e.data.error) || 'Could not load company MCP keys';
    }).finally(() => {
      cmKeysLoading.value = false;
      if(cmKeysRequest === request) cmKeysRequest = null;
    });
    cmKeysRequest = request;
    return request;
  }
  async function refreshCompanyMcpKeys(){
    if(cmKeysRequest) await cmKeysRequest;
    cmKeysLoaded.value = false;
    return loadCompanyMcpKeys(true);
  }
  effect(() => {
    if(tab.value === 'apikeys' && isCompanyAdmin(actorCompanyRole.value)) loadCompanyMcpKeys();
  });
  function onCompanyMcpScopeChange(scope, event){
    const next = cmScopes.value.slice(), index = next.indexOf(scope);
    if(event.target.checked && index < 0) next.push(scope);
    else if(!event.target.checked && index >= 0) next.splice(index, 1);
    cmScopes.value = next;
  }
  async function createCompanyMcpKey(e){
    e.preventDefault();
    if(cmBusy.value) return;
    cmErr.value = ''; cmSecret.value = '';
    if(!isCompanyAdmin(actorCompanyRole.value)){ cmErr.value = 'Only a company admin may manage company MCP keys'; return; }
    if(!cmName.value.trim()){ cmErr.value = t('mcp_keys.name_required', 'Please give the key a name'); return; }
    if(!cmScopes.value.length){ cmErr.value = t('mcp_keys.pick_scope', 'Choose at least one permission'); return; }
    cmBusy.value = true;
    try {
      const r = await api.post('/companies/' + companyId + '/settings/mcp-keys', {
        name: cmName.value.trim(), principal_kind: 'company', granted_level: cmLevel.value,
        environment: cmEnv.value, scopes: cmScopes.value.slice(),
        keeps_source_dates: cmKeepsSourceDates.value === true,
      });
      cmSecret.value = r.secret || '';
      cmName.value = ''; cmScopes.value = []; cmKeepsSourceDates.value = false;
      // Preserve the one-time secret if this independent metadata refresh fails.
      await refreshCompanyMcpKeys();
    } catch(e){ cmErr.value = (e.data && e.data.error) || t('mcp_keys.create_failed', 'Could not create the key'); }
    cmBusy.value = false;
  }
  async function revokeCompanyMcpKey(id){
    if(!isCompanyAdmin(actorCompanyRole.value)) return;
    cmRevokeBusy.value = true;
    try {
      await api.post('/companies/' + companyId + '/settings/mcp-keys/' + id + '/revoke', {});
      cmRevokeId.value = null; await refreshCompanyMcpKeys();
    } catch(e){ cmKeysErr.value = (e.data && e.data.error) || 'Could not revoke company MCP key'; }
    cmRevokeBusy.value = false;
  }
  function copyMcpSecret(){ if(mSecret.value && navigator.clipboard) navigator.clipboard.writeText(mSecret.value); }
  function copyCompanyMcpSecret(){ if(cmSecret.value && navigator.clipboard) navigator.clipboard.writeText(cmSecret.value); }
  const copyText = text => { if(text && navigator.clipboard) navigator.clipboard.writeText(text); };

  // ── Per-user AI settings over GET/PUT/POST-test /companies/{id}/ai-settings (bank_ai_service),
  // ported verbatim from staging. The key is write-only (masked on read; a blank input keeps the
  // stored key). The provider catalog (models/hint/key-url) comes from the API so adding a provider
  // is a backend-only change. (Owner 2026-09-06: restore the AI provider key card to match staging.)
  async function loadAI(){
    if(aiLoaded.value) return;
    try {
      const r = await api.get('/companies/' + companyId + '/ai-settings');
      aiProviders.value = (r && r.providers) || {};
      aiProvider.value = (r && r.provider) || 'gemini';
      aiHasKey.value = !!(r && r.has_key);
      aiMasked.value = (r && (r.key_masked || r.gemini_key_masked)) || '';
      aiDefaultModel.value = (r && r.default_model) || '';
      aiModel.value = (r && (r.model || r.gemini_model)) || aiDefaultModel.value;
      aiPdfPw.value = (r && r.pdf_password) || '';
      aiThreshPct.value = Math.round((((r && r.ai_threshold) != null) ? r.ai_threshold : 0.9) * 100);
      aiServerKey.value = !!(r && r.server_key);
      aiDisabled.value = !!(r && r.ai_disabled);
      aiLoaded.value = true;
    } catch(e){ aiErr.value = (e.data && e.data.error) || t('settings.ai_load_failed', 'Could not load AI settings'); }
  }
  // Disable AI for this user: clears the stored key AND sets the per-user off-switch, so even the
  // shared platform key is ignored (server-side _ai_config). Enable clears the switch again.
  async function setAIDisabled(off){
    aiErr.value = ''; aiNote.value = ''; aiBusy.value = true;
    try {
      const body = off ? { clear_key: true, ai_disabled: true } : { ai_disabled: false };
      const r = await api.put('/companies/' + companyId + '/ai-settings', body);
      aiHasKey.value = !!(r && r.has_key);
      aiMasked.value = (r && (r.key_masked || r.gemini_key_masked)) || '';
      aiServerKey.value = !!(r && r.server_key);
      aiDisabled.value = !!(r && r.ai_disabled);
      aiKeyIn.value = ''; aiShowKey.value = false;
      aiEditKey.value = !off && !aiHasKey.value && !aiServerKey.value;  // on enable with no key, open the input
      aiNote.value = off ? t('settings.ai_disabled_done', 'AI disabled') : t('settings.ai_enabled_done', 'AI enabled');
      setTimeout(() => { if(aiNote.value.startsWith('AI ')) aiNote.value = ''; }, 3000);
    } catch(e){ aiErr.value = (e.data && e.data.error) || t('common.save_failed', 'Could not save'); }
    aiBusy.value = false;
  }
  async function saveAI(e){
    if(e) e.preventDefault();
    aiErr.value = ''; aiNote.value = '';
    aiBusy.value = true;
    try {
      const body = { provider: aiProvider.value, model: aiModel.value.trim(),
                     ai_threshold: aiThreshPct.value / 100, pdf_password: aiPdfPw.value };
      if(aiKeyIn.value.trim()) body.api_key = aiKeyIn.value.trim();   // blank keeps the stored key
      const r = await api.put('/companies/' + companyId + '/ai-settings', body);
      aiProvider.value = (r && r.provider) || aiProvider.value;
      aiHasKey.value = !!(r && r.has_key);
      aiMasked.value = (r && (r.key_masked || r.gemini_key_masked)) || '';
      aiModel.value = (r && (r.model || r.gemini_model)) || aiModel.value;
      if(r && r.ai_threshold != null) aiThreshPct.value = Math.round(r.ai_threshold * 100);
      aiServerKey.value = !!(r && r.server_key);
      aiDisabled.value = !!(r && r.ai_disabled);
      aiKeyIn.value = ''; aiShowKey.value = false; aiEditKey.value = false;  // never keep the raw key on screen
      aiNote.value = t('common.saved', 'Saved');
      setTimeout(() => { if(aiNote.value === t('common.saved', 'Saved')) aiNote.value = ''; }, 3000);
    } catch(e){ aiErr.value = (e.data && e.data.error) || t('common.save_failed', 'Could not save'); }
    aiBusy.value = false;
  }
  async function testAI(){
    aiErr.value = ''; aiNote.value = ''; aiTesting.value = true;
    try {
      const body = { provider: aiProvider.value };
      if(aiKeyIn.value.trim()) body.api_key = aiKeyIn.value.trim();   // test the typed key, else the stored one
      if(aiModel.value.trim()) body.model = aiModel.value.trim();
      const r = await api.post('/companies/' + companyId + '/ai-settings/test', body);
      aiNote.value = (r && r.message) || t('settings.ai_test_ok', 'Key is working');
    } catch(e){ aiErr.value = (e.data && e.data.error) || t('settings.ai_test_failed', 'Key test failed'); }
    aiTesting.value = false;
  }
  // #20 PayFast per-company creds — GET/PUT /settings/payfast (write-only key + passphrase).
  async function loadPayfast(){
    if(pfLoaded.value) return;
    try {
      const r = await api.get('/companies/' + companyId + '/settings/payfast');
      pfConfigured.value = !!(r && r.configured);
      pfMerchantId.value = (r && r.merchant_id) || '';
      pfHasKey.value = !!(r && r.has_merchant_key);
      pfHasPass.value = !!(r && r.has_passphrase);
      pfSandbox.value = r ? r.sandbox !== false : true;
      pfLoaded.value = true;
    } catch(e){ pfErr.value = (e.data && e.data.error) || t('settings.pf_load_failed', 'Could not load payment settings'); }
  }
  async function savePayfast(e){
    if(e) e.preventDefault();
    pfErr.value = ''; pfNote.value = ''; pfBusy.value = true;
    try {
      const body = { merchant_id: pfMerchantId.value.trim(), sandbox: pfSandbox.value };
      if(pfKeyIn.value.trim()) body.merchant_key = pfKeyIn.value.trim();   // blank keeps the stored secret
      if(pfPassIn.value.trim()) body.passphrase = pfPassIn.value.trim();
      const r = await api.put('/companies/' + companyId + '/settings/payfast', body);
      pfConfigured.value = !!(r && r.configured);
      pfMerchantId.value = (r && r.merchant_id) || '';
      pfHasKey.value = !!(r && r.has_merchant_key);
      pfHasPass.value = !!(r && r.has_passphrase);
      pfSandbox.value = r ? r.sandbox !== false : true;
      pfKeyIn.value = ''; pfPassIn.value = '';                             // never keep raw secrets on screen
      pfNote.value = t('common.saved', 'Saved');
      setTimeout(() => { if(pfNote.value === t('common.saved', 'Saved')) pfNote.value = ''; }, 3000);
    } catch(e){ pfErr.value = (e.data && e.data.error) || t('common.save_failed', 'Could not save'); }
    pfBusy.value = false;
  }

  async function loadComm(){
    if(commLoaded.value) return;
    try {
      const r = await api.get('/companies/' + companyId + '/settings/communications');
      templates.value = r.templates || []; messages.value = r.messages || []; commLoaded.value = true;
    } catch(e){ toast(tErr, (e.data && e.data.error) || t('settings.comm_load_failed', 'Could not load communications')); }
  }
  function openNewT(){ tEditId.value = null; tCode.value = ''; tName.value = ''; tKind.value = 'invoice';
    tSubject.value = ''; tBody.value = ''; tActive.value = true; tErr.value = ''; tOpen.value = true; }
  function openEditT(t){ tEditId.value = t.id; tCode.value = t.code; tName.value = t.name; tKind.value = t.kind;
    tSubject.value = t.subject; tBody.value = t.body || ''; tActive.value = !!t.is_active; tErr.value = ''; tOpen.value = true; }
  function closeT(){ tOpen.value = false; tEditId.value = null; tErr.value = ''; }
  async function saveT(e){
    e.preventDefault(); tBusy.value = true; tErr.value = '';
    try {
      if(tEditId.value){
        await api.put('/companies/' + companyId + '/settings/communications/templates/' + tEditId.value,
          { name: tName.value, kind: tKind.value, subject: tSubject.value, body: tBody.value, is_active: tActive.value });
      } else {
        await api.post('/companies/' + companyId + '/settings/communications/templates',
          { code: tCode.value, name: tName.value, kind: tKind.value, subject: tSubject.value, body: tBody.value, is_active: tActive.value });
      }
      closeT(); commLoaded.value = false; await loadComm();
    } catch(e){ tErr.value = (e.data && e.data.error) || t('settings.template_save_failed', 'Could not save the template'); }
    tBusy.value = false;
  }
  async function doDeleteT(id){
    tDelBusy.value = true; tDelErr.value = '';
    try { await api.post('/companies/' + companyId + '/settings/communications/templates/' + id + '/delete', {});
      tDelId.value = null; commLoaded.value = false; await loadComm(); }
    catch(e){ tDelErr.value = (e.data && e.data.error) || t('common.delete_failed', 'Delete failed'); }
    tDelBusy.value = false;
  }

  const filtered = () => {
    const s = search.value.trim().toLowerCase(), t = typeF.value;
    return accounts().filter(a => {
      if(t === 'is' && a.statement !== 'IS') return false;
      if(t === 'bs' && a.statement !== 'BS') return false;
      if(t === 'bank' && !a.is_bank) return false;
      if(s && !((a.code || '').toLowerCase().includes(s) || (a.name || '').toLowerCase().includes(s))) return false;
      return true;
    });
  };

  // ── tab: Company ──
  // Renders inside the ${view} slot: bundle-derived values are read HERE (plain values);
  // the only inner reactive slots depend on signals view() never reads (safe — disjoint deps).
  // iT structure: one form wrapping a card per topic (h3 + small, grid2/fld fields, hints),
  // one Save in the form-actions at the bottom — the sandbox's own settings shape.
  const companyTab = () => {
    // Currencies + FX rates moved to their own tab (owner Andre 2026-09-17: "add forex as its
    // own tab at the top when we toggle forex on"). See forexTab() below. companyTab keeps
    // only identity / address / locale / FY start / VAT / toggles + the support panel.
    // support access — same read-every-signal-at-the-top discipline as the other panels
    const supCodeV = supCode.value, supExpV = supExpiresAt.value, supBusyV = supBusy.value, supErrV = supErr.value;
    supTick.value;   // subscribe — ticks the mm:ss countdown text every second
    // the standard the company was created with, in the add-company form's words; '' (no line) from an API that
    // does not send it
    const stdV = cStd.value;
    const stdText = stdV.std === 'GAAP' ? 'GAAP · ' + t('settings.std_gaap', "{country}'s own rules").replace('{country}', stdV.country)
      : stdV.std === 'IFRS' ? 'IFRS · ' + t('settings.std_ifrs', 'International standards') : '';
    const supportPanel = html`<div class="card">
      <h3>${t('support.title', 'Support access')} <small>${t('support.small', 'one-time code for aboo support')}</small></h3>
      <p class="hint">${t('support.help', 'Generate a one-time code and read it to aboo support — it lets an administrator view this account for one hour.')}</p>
      ${supCodeV ? html`
        <div class="support-code-box">
          <div class="support-code">${supCodeV}</div>
          <div class="support-code-expiry">${t('support.expires_in', 'Expires in')} <span class="mono">${fmtCountdown(supExpV)}</span></div>
        </div>
        <div class="form-actions">
          <button type="button" class="btn" ?disabled=${supBusyV} @click=${generateSupportCode}>${supBusyV ? t('common.saving', 'Saving…') : t('support.regenerate', 'Regenerate')}</button>
          <button type="button" class="btn bad" ?disabled=${supBusyV} @click=${revokeSupportCode}>${t('support.revoke', 'Revoke')}</button>
        </div>`
        : html`<div class="form-actions">
          <button type="button" class="btn primary" ?disabled=${supBusyV} @click=${generateSupportCode}>${supBusyV ? t('common.saving', 'Saving…') : t('support.generate', 'Generate code')}</button>
        </div>`}
      ${supErrV ? html`<div class="row-err">${supErrV}</div>` : ''}
    </div>`;
    const f = html`<div class="set-stack"><form @submit=${saveCompany}>
    <div class="card">
      <h3>${t('settings.company_identity', 'Company identity')} <small>${t('settings.identity_small', 'shown on every document you send')}</small></h3>
      <div class="grid2">
        <label class="fld"><span>${t('common.company_name','Company name')}</span>
          <input .value=${cName} @input=${e => { cName.value = e.target.value; }} placeholder=${t('settings.eg_company_name', 'e.g. Riverside Trading (Pty) Ltd')}></label>
        <label class="fld"><span>${t('settings.registration_no', 'Registration number')}</span>
          <input .value=${cReg} @input=${e => { cReg.value = e.target.value; }} placeholder=${t('settings.eg_reg_no', 'e.g. 2019/123456/07')}></label>
        <label class="fld"><span>${t('common.email','Email')}</span>
          <input type="email" .value=${cEmail} @input=${e => { cEmail.value = e.target.value; }} placeholder=${t('settings.eg_email', 'e.g. accounts@riverside.co.za')}></label>
        <label class="fld"><span>${t('common.phone','Phone')}</span>
          <input .value=${cPhone} @input=${e => { cPhone.value = e.target.value; }} placeholder=${t('settings.eg_phone', 'e.g. 021 555 1234')}></label>
      </div>
    </div>
    <div class="card">
      <h3>${t('common.address','Address')}</h3>
      <div class="grid2">
        <label class="fld"><span>${t('common.street_address','Street address')}</span>
          <input .value=${cAddr1} @input=${e => { cAddr1.value = e.target.value; }} placeholder=${t('settings.eg_addr1', 'e.g. 12 Riverside Road')}></label>
        <label class="fld"><span>${t('settings.address_line2', 'Address line 2')}</span>
          <input .value=${cAddr2} @input=${e => { cAddr2.value = e.target.value; }} placeholder=${t('settings.eg_addr2', 'e.g. Unit 4, Riverside Park')}></label>
        <label class="fld"><span>${t('settings.town_city', 'Town / city')}</span>
          <input .value=${cTown} @input=${e => { cTown.value = e.target.value; }} placeholder=${t('settings.eg_town', 'e.g. Cape Town')}></label>
        <label class="fld"><span>${t('settings.postal_code', 'Postal code')}</span>
          <input .value=${cPostal} @input=${e => { cPostal.value = e.target.value; }} placeholder=${t('settings.eg_postal', 'e.g. 8001')}></label>
      </div>
    </div>
    <div class="card">
      <h3>${t('settings.locale_currency', 'Locale & currency')} <small>${t('settings.locale_small', 'language, base currency and financial year')}</small></h3>
      <div class="grid2">
        <label class="fld"><span>${t('common.language','Language')}</span>
          <span data-lang-cell></span></label>
        <label class="fld"><span>${t('settings.default_currency', 'Default currency')}</span>
          <span data-cur-cell></span></label>
        <label class="fld"><span>${t('settings.fy_starts', 'Financial year starts')}</span>
          <div class="set-fy-row">
            <select class="set-fy-day" .value=${cFyDay} @change=${e => { cFyDay.value = e.target.value; }}>
              ${Array.from({ length: 31 }, (_, i) => html`<option value=${String(i + 1)}>${i + 1}</option>`)}</select>
            <select .value=${cFyMonth} @change=${e => { cFyMonth.value = e.target.value; }}>
              ${MONTHS.map((m, i) => html`<option value=${String(i + 1)}>${m}</option>`)}</select>
          </div></label>
        ${stdText ? html`<label class="fld"><span>${t('settings.accounting_standard', 'Accounting standard')}</span>
          <input readonly .value=${stdText} placeholder=${t('settings.eg_standard', 'e.g. IFRS · International standards')}></label>` : ''}
      </div>
      ${stdText ? html`<div class="hint">${t('settings.accounting_standard_help', "Chosen when the company was created; it can't be changed.")}</div>` : ''}
    </div>
    <div class="card">
      <h3>${t('settings.vat_tax', 'VAT & tax')}</h3>
      <div class="grid2">
        <label class="fld inline"><span>${t('settings.vat_registered', 'VAT registered')}</span>
          <input type="checkbox" class="chk" .checked=${cVatReg} @change=${e => { cVatReg.value = e.target.checked; }}></label>
        <span></span>
        <label class=${() => 'fld' + (cVatReg.value ? '' : ' jrn-hide')}><span>${t('common.vat_number','VAT number')}</span>
          <input .value=${cVatNo} @input=${e => { cVatNo.value = e.target.value; if(coNote.value) coNote.value = ''; }} placeholder=${t('settings.eg_vat_no', 'e.g. 4990001234')}></label>
        <label class="fld"><span>${t('common.tax_number','Tax number')}</span>
          <input .value=${cTaxNo} @input=${e => { cTaxNo.value = e.target.value; }} placeholder=${t('settings.eg_tax_no', 'e.g. 9012345678')}></label>
      </div>
    </div>
    <div class="card">
      <h3>${t('settings.doc_terms', 'Document terms')} <small>${t('settings.doc_terms_small', 'default validity and payment terms on new documents')}</small></h3>
      <div class="grid2">
        <label class="fld"><span>${t('settings.quote_valid_days', 'Quote valid for (days)')}</span>
          <input type="number" min="0" max="365" .value=${cQuoteDays} @input=${e => { cQuoteDays.value = e.target.value; if(coNote.value) coNote.value = ''; }} placeholder="30"></label>
        <label class="fld"><span>${t('settings.invoice_due_days', 'Invoice due by (days)')}</span>
          <input type="number" min="0" max="365" .value=${cInvoiceDays} @input=${e => { cInvoiceDays.value = e.target.value; if(coNote.value) coNote.value = ''; }} placeholder="30"></label>
        <label class="fld"><span>${t('settings.default_sales_account', 'Default sales account')}</span>
          <span data-defsales-cell></span></label>
        <label class="fld"><span>${t('settings.default_purchase_account', 'Default purchase account')}</span>
          <span data-defpurch-cell></span></label>
      </div>
      <div class="hint">${t('settings.doc_terms_help', 'A new quote’s valid-until date and a new invoice’s due date default to the document date plus these many days.')}</div>
      <div class="hint">${t('settings.default_accounts_help', 'A free-form document line (no stock item) posts to these accounts by default — sales side for invoices/quotes/credit notes, purchase side for bills/orders. Overridable per line; leave blank to add the account manually.')}</div>
    </div>
    <div class="card">
      <h3>${t('settings.ledger_display', 'Ledger display')} <small>${t('settings.ledger_small', 'how accounts and currencies behave')}</small></h3>
      <div class="grid2">
        <label class="fld inline"><span>${t('settings.enable_subaccounts', 'Enable sub-accounts (0000.000 codes)')}</span>
          <input type="checkbox" class="chk" .checked=${cSub} @change=${e => { cSub.value = e.target.checked; }}></label>
        <label class="fld inline"><span>${t('settings.allow_multi_currency', 'Allow bank accounts in other currencies (USD, EUR …)')}</span>
          <input type="checkbox" class="chk" .checked=${cMulti} @change=${e => { cMulti.value = e.target.checked; }}></label>
      </div>
    </div>
    <div class="card">
      <h3>${t('settings.bank_imports', 'Bank imports')}</h3>
      <label class="fld inline"><span>${t('settings.auto_post', 'Auto-post cleanly-allocated imported entries')}</span>
        <input type="checkbox" class="chk" .checked=${cAutoPost} @change=${e => { cAutoPost.value = e.target.checked; }}></label>
      <div class="hint">${t('settings.auto_post_help', 'On: a statement import posts its confidently-allocated, non-duplicate lines straight to the ledger (creating the journals); anything unsure still waits in review. Off: every imported line waits in review for you to post by hand.')}</div>
    </div>
    <div class="card">
      <h3>${t('settings.approvals', 'Expense approvals')} <small>${t('settings.approvals_small', 'who signs off on money out')}</small></h3>
      <label class="fld inline"><span>${t('settings.require_expense_approval', 'Require approval before an expense can be paid')}</span>
        <input type="checkbox" class="chk" .checked=${cApproval} @change=${e => { cApproval.value = e.target.checked; }}></label>
      <div class="hint">${t('settings.require_expense_approval_help', 'On: an expense a team member submits must be approved by an admin or governor before it can be paid. Off: an approver can pay a submitted expense directly. Approving/rejecting expenses happens on the Approvals screen.')}</div>
    </div>
    <div class="form-actions">
      <button type="submit" class="btn primary" ?disabled=${() => savingCo.value}>${() => savingCo.value ? t('common.saving', 'Saving…') : t('common.save_changes', 'Save Changes')}</button>
      ${() => coNote.value ? html`<div class="row-err">${coNote.value}</div>` : ''}
    </div>
  </form>${supportPanel}</div>`;
    f.querySelector('[data-lang-cell]').append(langSel.el);
    f.querySelector('[data-cur-cell]').append(curSel.el);
    f.querySelector('[data-defsales-cell]')?.append(defSalesSel.el);
    f.querySelector('[data-defpurch-cell]')?.append(defPurchSel.el);
    return f;
  };

  // ── tab: Forex (currencies + FX rates) — appears at the top of the tabs only when
  //         multi_currency_enabled is on (owner Andre 2026-09-17).
  const forexTab = () => {
    // Read every fx-related signal here as PLAIN VALUES so nothing nests a ${() => …} inside
    // the render fragment; inputs keep `.value=${signal}` bindings, which are property binds.
    const rs = rates.value, rErrV = rErr.value, rBusyV = rBusy.value,
          revalBusyV = revalBusy.value, revalNoteV = revalNote.value;
    const activeSet = activeCur.value,
          allCur = (bundle.value || {}).currencies || [], baseId = baseCurId.value,
          baseCode = (allCur.find(c => c.id === baseId) || {}).code || '', curNoteV = curNote.value;

    const f = html`<div class="set-stack">
      <div class="card cur-card" data-curlist>
        <div class="cur-head">
          <h3>${t('settings.currencies', 'Currencies')}</h3>
          <span class="cur-sub">${t('settings.currencies_sub', 'Toggle currencies your company uses')}</span>
        </div>
        <div class="tbl-wrap"><table class="list cur-table">
          <thead><tr>
            <th>${t('settings.cur_code', 'Code')}</th>
            <th>${t('settings.cur_symbol', 'Symbol')}</th>
            <th>${t('common.name', 'Name')}</th>
            <th class="ta-r">${t('settings.cur_rate_vs', 'Rate vs')} ${baseCode}</th>
            <th class="ta-r">${t('common.status', 'Status')}</th>
          </tr></thead>
          <tbody>${allCur.map(cu => {
            const isBase = cu.id === baseId, on = isBase || activeSet.has(cu.id);
            return html`<tr class=${'cur-row' + (isBase ? ' is-base' : '')}>
              <td><b>${cu.code}</b></td>
              <td class="cur-sym">${cu.symbol || ''}</td>
              <td>${cu.name || ''}</td>
              <td class="ta-r num">${isBase ? html`<span class="cur-tag base">${t('settings.cur_base', 'BASE')}</span>` : fmtRate(rateVsBase(cu.code, baseCode, rs))}</td>
              <td class="ta-r">${isBase
                ? html`<span class="cur-pill base">${t('settings.cur_base', 'BASE')}</span>`
                : html`<button type="button" class=${'cur-pill toggle' + (on ? ' active' : '')} data-cur-id=${String(cu.id)} aria-pressed=${on ? 'true' : 'false'} @click=${() => toggleCur(cu.id)}>${on ? t('settings.cur_active', 'Active') : t('settings.cur_inactive', 'Inactive')}</button>`}</td>
            </tr>`;
          })}</tbody>
        </table></div>
        ${curNoteV ? html`<div class="row-err cur-note">${curNoteV}</div>` : ''}
      </div>

      <div class="card fx-card">
        <h3>${t('settings.exchange_rates', 'Exchange rates')} <small>${t('settings.fx_small', 'manual rates for foreign bank entries')}</small></h3>
        <p class="hint">${t('settings.fx_help', 'Rates convert foreign bank entries to your base currency when they post. Capture the rate for (or just before) each transaction date — a foreign entry with no rate on file stays in review rather than posting at 1:1.')}</p>
        <form class="fx-add" @submit=${addRate}>
          <div class="grid2">
            <label class="fld"><span>${t('settings.from_currency', 'From currency')}</span><span data-fx-from></span></label>
            <label class="fld"><span>${t('settings.to_currency', 'To currency (base)')}</span><span data-fx-to></span></label>
            <label class="fld"><span>${t('common.date','Date')}</span>
              <sleek-date data-free="true" class="form-control" .value=${rDate} @change=${e => { rDate.value = e.target.value; clearFxNotice(); }}></sleek-date></label>
            <label class="fld"><span>${t('settings.fx_rate_lbl', 'Rate (1 from = ? to)')}</span>
              <input type="text" inputmode="decimal" .value=${rRate} @input=${e => { rRate.value = e.target.value; clearFxNotice(); }} placeholder=${t('settings.eg_fx_rate', 'e.g. 18.5000')}></label>
          </div>
          <div class="form-actions">
            <button type="submit" class="btn primary" ?disabled=${rBusyV}>${rBusyV ? t('common.saving', 'Saving…') : t('settings.add_rate', 'Add rate')}</button>
            ${rErrV ? html`<div class="row-err">${rErrV}</div>` : ''}
          </div>
        </form>
        ${rs.length ? html`<div class="tbl-wrap"><table class="list"><thead><tr><th>${t('common.date','Date')}</th><th>${t('settings.fx_pair', 'Pair')}</th>
          <th class="ta-r">${t('common.rate','Rate')}</th><th>${t('settings.fx_scope', 'Scope')}</th><th>${t('common.source','Source')}</th></tr></thead>
          <tbody>${rs.map(r => html`<tr><td>${r.rate_date}</td><td>${r.from_code} → ${r.to_code}</td>
            <td class="ta-r num">${r.rate}</td>
            <td>${r.is_override ? html`<span class="fx-scope fx-scope-own">${t('settings.fx_yours', 'Yours')}</span>`
                                : html`<span class="fx-scope">${t('settings.fx_shared', 'Shared')}</span>`}</td>
            <td>${r.source || 'manual'}</td></tr>`)}</tbody></table></div>`
          : html`<div class="muted-block">${t('settings.fx_no_rates', 'No rates captured yet.')}</div>`}
        <div class="form-actions fx-reval">
          <button type="button" class="btn" ?disabled=${revalBusyV} @click=${doRevalue}>${revalBusyV ? t('settings.revaluing', 'Revaluing…') : t('settings.revalue_balances', 'Revalue foreign balances')}</button>
          ${revalNoteV ? html`<div class="row-err">${revalNoteV}</div>` : ''}
        </div>
      </div>
    </div>`;
    const ff = f.querySelector('[data-fx-from]'), ft = f.querySelector('[data-fx-to]');
    if(ff) ff.append(fxFromSel.el);
    if(ft) ft.append(fxToSel.el);
    return f;
  };

  // Which tabs are visible right now — Forex slots in after Branding & Comms when
  // multi_currency is on (owner Andre 2026-09-17: "move forex to after branding and comms").
  const visibleTabs = () => {
    if(!cMulti.value) return BASE_TABS;
    const out = [];
    for(const t of BASE_TABS){
      out.push(t);
      if(t[0] === 'branding') out.push(FOREX_TAB);
    }
    return out;
  };

  // ── tab: Chart of Accounts (aboo-schema — iT restyle only, controls unchanged) ──
  // accForm/coaTab render INSIDE the outer ${view} reactive slot, so every signal is read
  // at the top and interpolated as a plain value — a nested ${() => …} here APPENDS its
  // output on each outer re-render (the tina4 duplicate-grid bug the owner hit).
  const accForm = () => {
    const editing = !!editId.value, err = aErr.value, busyA = aBusy.value;
    // type/tax/contra are searchSelects (owner: dropdown parity + a chevron). They are stable nodes
    // created once above; we build the form fragment, then move each .el into its cell and return —
    // the same idempotent append the company pickers use, so an outer re-render never traps focus.
    const f = html`<form class="card set-accform" @submit=${saveAccount}>
    <h3>${editing ? t('settings.edit_account', 'Edit account') : t('settings.new_account', 'New account')}</h3>
    <div class="grid2">
      <label class="fld"><span>${t('common.gl_code','GL code')}</span>
        <input .value=${aCode} @input=${e => { aCode.value = e.target.value; }} placeholder="4 letters or digits e.g. 7799" maxlength="4" ?disabled=${editing}></label>
      <label class="fld"><span>${t('common.description','Description')}</span>
        <input .value=${aName} @input=${e => { aName.value = e.target.value; }} placeholder=${t('settings.eg_account_name', 'e.g. Sundry Expenses')}></label>
      ${editing ? '' : html`<label class="fld"><span>${t('settings.account_type', 'Account type')}</span>
        <span data-acc-type-cell></span></label>`}
      <label class="fld"><span>${t('settings.default_tax_code', 'Default tax code')}</span>
        <span data-acc-tax-cell></span></label>
      <label class="fld"><span>${t('settings.contra_account', 'Contra account')}</span>
        <span data-acc-contra-cell></span>
        <span class="hint">${t('settings.contra_hint', 'The other side to auto-fill when you post a journal to this account.')}</span></label>
      <label class="fld inline"><span>${t('settings.is_bank_account', 'This is a bank account')}</span>
        <input type="checkbox" class="chk" .checked=${aBank} @change=${e => { aBank.value = e.target.checked; }}></label>
    </div>
    ${err ? html`<div class="row-err">${err}</div>` : ''}
    <div class="form-actions">
      <button type="submit" class="btn primary" ?disabled=${busyA}>${busyA ? t('common.saving', 'Saving…') : (editing ? t('common.save', 'Save') : t('settings.add_account', 'Add'))}</button>
      <button type="button" class="btn" @click=${closeForm}>${t('common.cancel','Cancel')}</button>
    </div>
  </form>`;
    if(!editing){ const tc = f.querySelector('[data-acc-type-cell]'); if(tc) tc.append(typeSel.el); }
    const xc = f.querySelector('[data-acc-tax-cell]'); if(xc) xc.append(taxSel.el);
    const cc = f.querySelector('[data-acc-contra-cell]'); if(cc) cc.append(contraSel.el);
    return f;
  };

  const coaTab = () => {
    const all = filtered(), cd = confirmDel.value, fo = formOpen.value;
    const reB = reBusy.value, reN = reNote.value;
    const ro = restampOffer.value, roB = restampBusy.value;   // re-stamp offer after a VAT-code change
    const per = Number(coaPer.value), totalPages = Math.max(1, Math.ceil(all.length / per));
    const pg = Math.min(coaPage.value, totalPages);
    const rows = all.slice((pg - 1) * per, (pg - 1) * per + per);
    const reSel = accounts().find(a => String(a.id) === String(reAcct.value));
    const reLbl = reSel ? gcode(reSel.code) + ' — ' + reSel.name : '';
    const pgBar = () => pager({ page: pg, totalPages, total: all.length,
                                perPage: per, onPage: p => { coaPage.value = p; } });
    return html`<div>
      ${ro ? html`<div class="alert set-restamp-offer">
        <span>${t('settings.restamp_offer', 'The VAT code changed.')} ${ro.count}
          ${t('settings.restamp_lines', 'unposted bank line(s) allocated to this account still carry the old code.')}</span>
        <span class="set-restamp-actions">
          <button class="btn btn-sm btn-primary" ?disabled=${roB} @click=${doRestamp}>${roB ? t('common.working', 'Working…') : t('settings.restamp_now', 'Re-stamp them')}</button>
          <button class="btn btn-sm btn-outline" @click=${() => { restampOffer.value = null; }}>${t('common.not_now', 'Not now')}</button>
        </span>
      </div>` : null}
      <div class="list-toolbar set-coa-bar">
        <input id="setCoaSearch" class="search ux-search" type="search" placeholder=${t('settings.coa_search_ph', 'Search code or description…')}
          .value=${search.value} @input=${e => {
            // This input lives in the reactive coaTab view, so writing search.value (needed to
            // re-filter) re-renders the tab and RECREATES this node. Write NOTHING on the keystroke
            // itself — the field just accumulates text with no re-render — and only on the debounce
            // commit the filter, then RESTORE the field's latest value + caret + focus on the node
            // the re-render recreated. Dropping every-char-after-the-first is gone.
            const v = e.target.value, pos = e.target.selectionStart;
            clearTimeout(coaSearchTimer);
            coaSearchTimer = setTimeout(() => {
              search.value = v; coaPage.value = 1;
              setTimeout(() => {
                const el = document.getElementById('setCoaSearch');
                if(el){ if(el.value !== v) el.value = v; el.focus(); try { el.setSelectionRange(pos, pos); } catch { /* type=search quirk */ } }
              }, 0);
            }, 250);
          }}>
        <select class="set-typef" .value=${typeF.value}
          @change=${e => { typeF.value = e.target.value; coaPage.value = 1; }}>
          <option value="all">${t('settings.all_accounts', 'All accounts')}</option><option value="bs">${t('reports.balance_sheet', 'Balance Sheet')}</option>
          <option value="is">${t('reports.income_statement', 'Income Statement')}</option><option value="bank">${t('common.bank_accounts','Bank accounts')}</option>
        </select>
        ${perPageSelect(coaPer.value, e => { coaPer.value = e.target.value; coaPage.value = 1; })}
        <span class="jrn-spacer"></span>
        <button class="btn primary" @click=${openNew}>+ ${t('settings.new_account', 'New Account')}</button>
      </div>
      ${fo ? accForm() : ''}
      <div class="card set-re-card">
        <h3>${t('settings.year_end_account', 'Year-end (retained earnings) account')}</h3>
        <div class="set-re">
          <input class="set-re-sel" type="text" data-float-anchor .value=${reLbl}
            placeholder=${t('settings.year_end_ph', 'Search a balance-sheet account… e.g. 5100 or Retained')}
            @input=${e => showReDrop(e.target)} @keydown=${reKeydown}>
          <button class="btn" ?disabled=${reB} @click=${saveRetained}>${t('common.save','Save')}</button>
          ${reN ? html`<div class="row-err">${reN}</div>` : ''}
        </div>
      </div>
      ${pgBar()}
      <div class="tbl-wrap"><table class="list set-coa">
        <thead><tr><th>${t('common.gl_code','GL Code')}</th><th>${t('common.description','Description')}</th><th>${t('common.group','Group')}</th><th>${t('common.tax','Tax')}</th><th>${t('settings.bank_col', 'Bank')}</th><th class="ta-r">${t('common.balance','Balance')}</th><th class="ta-r">${t('settings.txns_col', 'Txns')}</th><th></th></tr></thead>
        <tbody>${rows.length ? rows.map(a => html`<tr>
          <td class="mono">${gcode(a.code)} <span class=${'set-stmt set-' + a.statement}>${a.statement}</span></td>
          <td>${a.name}${a.is_control ? html` <span class="muted">· control</span>` : ''}</td>
          <td class="muted">${a.group_name || ''}</td>
          <td class="mono" data-label=${t('common.tax','Tax')}>${a.tax_code || ''}</td>
          <td data-label=${t('settings.bank_col', 'Bank')}>${a.is_bank ? '✓' : ''}</td>
          <td class="mono ta-r" data-label=${t('common.balance','Balance')}><span class=${Number(a.balance) < 0 ? 'num-neg' : ''}>${fmtRS(a.balance)}</span></td>
          <td class="mono ta-r" data-label=${t('settings.txns_col', 'Txns')}>${a.txn_count}</td>
          <td class="ta-r nowrap set-acts">
            <button class="icon-btn" title=${t('common.edit','Edit')} @click=${() => openEdit(a)}>✎</button>
            ${rowDelete({ armed: cd === a.id, busy: delBusy.value, error: cd === a.id ? delErr.value : '',
              onArm: () => { delErr.value = ''; confirmDel.value = a.id; }, onCancel: () => { confirmDel.value = null; },
              onDelete: () => doDelete(a.id) })}
          </td></tr>`)
          : html`<tr><td colspan="8" class="muted">${t('settings.no_accounts_match', 'No accounts match.')}</td></tr>`}</tbody>
      </table></div>
      ${pgBar()}
      <div class="set-coa-count">${all.length} of ${accounts().length} accounts</div>
    </div>`;
  };

  // ── tab: Branding ──
  const colorRow = (lbl, sig, ph) => html`<label class="fld"><span>${lbl}</span>
    <div class="bt-color-row">
      <input type="color" class="bt-swatch" .value=${() => sig.value || '#000000'} @input=${e => { sig.value = e.target.value; }}>
      <input class="bt-hex" .value=${sig} @input=${e => { sig.value = e.target.value; }} placeholder=${ph}>
    </div></label>`;
  const brandingTab = () => html`<form @submit=${saveBranding}>
    <div class="card">
      <h3>${t('settings.brand_appearance', 'Brand & document appearance')} <small>${t('settings.brand_small', 'logo, colours and standard wording')}</small></h3>
      <!-- Not a <label> wrapper: it contains the hidden file input, so a native label click would
           double-fire with our dropzone @click and open the picker twice. -->
      <div class="fld"><span>${t('settings.logo', 'Logo')}</span>
        <div class="dropzone bt-logo-drop" @click=${() => { const el = document.getElementById('bt-logo-file'); if(el) el.click(); }}
          @dragover=${onLogoDragOver} @dragleave=${onLogoDragLeave} @drop=${onLogoDrop}>
          ${() => bLogo.value.trim()
            ? html`<img class="bt-logo-drop-img" src=${bLogo.value.trim()} alt=${t('settings.logo_preview', 'Logo preview')}>
                   <div class="dropzone-hint">${t('settings.logo_replace', 'Click or drop an image to replace')}</div>`
            : html`<div class="dropzone-title">${t('settings.logo_drop_title', 'Drag a logo here, or click to browse')}</div>
                   <div class="dropzone-hint">${t('settings.logo_drop_hint', 'PNG, JPG or SVG — shown on your invoices')}</div>`}
        </div>
        <input id="bt-logo-file" class="bt-logo-file" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
          aria-label=${t('settings.upload_logo', 'Upload logo')} placeholder=${t('settings.eg_logo_file', 'e.g. riverside-logo.png')} @change=${onLogoFile}>
        ${() => logoErr.value ? html`<div class="row-err">${logoErr.value}</div>` : ''}
        ${() => bLogo.value.trim() ? html`<div class="bt-logo-actions">
          <button type="button" class="btn btn-outline btn-sm" @click=${clearLogo}>${t('settings.remove_logo', 'Remove')}</button></div>` : ''}
        <!-- An UPLOADED image (data: URI) shows a static label, NOT an input — binding an input's
             value to a masked-empty computed could feed that empty value back into bLogo and WIPE
             the logo on save (owner 2026-09-19: "logo is not being saved when overwriting"). The URL
             paste input appears only when the logo is a real URL or not set yet, bound to bLogo. -->
        ${() => (bLogo.value || '').startsWith('data:')
          ? html`<div class="bt-logo-url bt-logo-uploaded">${t('settings.logo_uploaded', 'Uploaded image — click Remove to change it')}</div>`
          : html`<input class="bt-logo-url" .value=${bLogo}
              @input=${e => { bLogo.value = e.target.value; }}
              placeholder=${t('settings.eg_logo_url', 'or paste a URL, e.g. https://cdn.aboo.co.za/riverside-logo.png')}>`}</div>
      <div class="grid2">
        ${colorRow(t('settings.primary_colour', 'Primary colour'), bPrimary, t('settings.eg_primary_hex', 'e.g. #19C4C4'))}
        ${colorRow(t('settings.secondary_colour', 'Secondary colour'), bSecondary, t('settings.eg_secondary_hex', 'e.g. #0A3D62'))}
      </div>
      <label class="fld"><span>${t('settings.terms_conditions','Terms & conditions')}</span>
        <textarea class="bt-area" rows="3" .value=${bTerms} @input=${e => { bTerms.value = e.target.value; }} placeholder=${t('settings.eg_terms', 'e.g. Goods remain the property of the seller until paid in full.')}></textarea></label>
      <label class="fld"><span>${t('common.payment_terms','Payment terms')}</span>
        <textarea class="bt-area" rows="3" .value=${bPayTerms} @input=${e => { bPayTerms.value = e.target.value; }} placeholder=${t('settings.eg_pay_terms', 'e.g. Payment due within 30 days of invoice date.')}></textarea></label>
      <label class="fld"><span>${t('settings.doc_footer', 'Document footer')}</span>
        <textarea class="bt-area" rows="3" .value=${bFooter} @input=${e => { bFooter.value = e.target.value; }} placeholder=${t('settings.eg_footer', 'e.g. Thank you for your business.')}></textarea></label>
    </div>
    <div class="card">
      <h3>${t('settings.remittance_title', 'Remittance (bank details printed on documents)')} <small>${t('settings.remit_small', 'how customers pay you — fields shown suit your country; SWIFT/IBAN are for international payments')}</small></h3>
      <!-- Feature 1.3 — the field set is country-aware (ZA branch code, US routing, UK sort code,
           EU IBAN+BIC). Reactive slot keyed ONLY on bCountry (loads once) — each input still binds
           its own signal, so typing never re-runs this slot (focus-safe). -->
      <div class="grid2">${() => remittanceFields(bCountry.value).map(f => html`
        <label class="fld"><span>${f.label}</span>
          <input class=${f.mono ? 'mono' : ''} .value=${remitSignals[f.key]}
            @input=${e => { remitSignals[f.key].value = e.target.value; }} placeholder=${f.placeholder}></label>`)}</div>
    </div>
    <div class="form-actions">
      <button type="submit" class="btn primary" ?disabled=${() => bBusy.value}>${() => bBusy.value ? t('common.saving', 'Saving…') : t('settings.save_branding', 'Save branding')}</button>
      ${() => bNote.value ? html`<div class="row-err">${bNote.value}</div>` : ''}
    </div>
  </form>`;

  // ── tab: Reminders (documents parity X2) ──
  async function loadReminders(){
    if(remLoaded.value) return;
    try {
      const r = await api.get('/companies/' + companyId + '/reminders/settings');
      remEnabled.value = !!(r && r.enabled);
      remOff.value = _sanitize((r && r.offsets) || []);
      remQuoteEnabled.value = !!(r && r.quote_enabled);
      remQuoteOff.value = _sanitize((r && r.quote_offsets) || []);
    } catch { remOff.value = [7, 14, 21]; remQuoteOff.value = [3, 7, 14]; }
    remLoaded.value = true;
  }
  // day-tier list → sorted, de-duped whole days in 1–365 (accepts an array or a legacy CSV string)
  function _sanitize(v){
    const arr = Array.isArray(v) ? v : String(v || '').split(',');
    return [...new Set(arr.map(x => parseInt(x, 10)).filter(n => n >= 1 && n <= 365))].sort((a, b) => a - b);
  }
  async function saveReminders(e){
    e.preventDefault();
    remErr.value = ''; remNote.value = '';
    const offsets = _sanitize(remOff.value);
    if(remEnabled.value && !offsets.length){ remErr.value = t('reminders.err_offsets', 'Add at least one day tier (1–365) for overdue reminders.'); return; }
    const quoteOffsets = _sanitize(remQuoteOff.value);
    if(remQuoteEnabled.value && !quoteOffsets.length){ remErr.value = t('reminders.err_quote_offsets', 'Add at least one day tier (1–365) for quote follow-ups.'); return; }
    remBusy.value = true;
    try {
      const r = await api.put('/companies/' + companyId + '/reminders/settings',
        { enabled: remEnabled.value, offsets: offsets.length ? offsets : [7, 14, 21],
          quote_enabled: remQuoteEnabled.value, quote_offsets: quoteOffsets.length ? quoteOffsets : [3, 7, 14] });
      remOff.value = _sanitize((r && r.offsets) || offsets);
      remQuoteOff.value = _sanitize((r && r.quote_offsets) || quoteOffsets);
      notice(t('reminders.saved', 'Reminder settings saved.'));
    } catch(err){ remErr.value = (err.data && err.data.error) || t('reminders.save_failed', 'Could not save reminder settings'); }
    remBusy.value = false;
  }
  async function sendDueReminders(){
    remRunning.value = true; remRun.value = null; remErr.value = '';
    try {
      const r = await api.post('/companies/' + companyId + '/reminders/run-due', {});
      remRun.value = r || { count: 0, sent: [], skipped: [] };
    } catch(err){ remErr.value = (err.data && err.data.error) || t('reminders.run_failed', 'Could not send due reminders'); }
    remRunning.value = false;
  }
  // Day-tier editor: an inline caption, removable day pills, and a number "Add" box (replaces the old
  // comma text field — owner: "needs interface, not hand entered"). No on/off toggle — adding a day
  // means you want it as a reminder (owner 2026-09-15); the ✕ removes a tier. Only the pill list is a
  // reactive slot; the caption + add-box stay static so typing keeps focus (tina4 focus-trap rule).
  function dayTierField(days, addVal, caption, addPlaceholder){
    const add = () => {
      const n = parseInt(String(addVal.value).trim(), 10);
      if(!(n >= 1 && n <= 365)) return;
      days.value = [...new Set([...days.value, n])].sort((a, b) => a - b);
      addVal.value = '';
      if(remErr.value) remErr.value = '';
    };
    const remove = d => { days.value = days.value.filter(x => x !== d); };
    return html`<div class="remday-field">
      <span class="remday-cap">${caption}</span>
      ${() => days.value.map(d => html`<span class="remday-chip">${d}<button type="button" class="remday-x" title=${t('reminders.remove_tier', 'Remove')} aria-label=${t('reminders.remove_tier', 'Remove') + ' ' + d} @click=${() => remove(d)}>✕</button></span>`)}
      <span class="remday-add">
        <input type="text" inputmode="numeric" maxlength="3" class="form-control remday-in" .value=${addVal}
          placeholder=${addPlaceholder} @input=${e => { addVal.value = e.target.value; }}
          @keydown=${e => { if(e.key === 'Enter'){ e.preventDefault(); add(); } }}>
        <button type="button" class="btn btn-sm remday-add-btn" @click=${add}>+ ${t('reminders.add_tier', 'Add')}</button>
      </span>
    </div>`;
  }
  const remindersTab = () => html`<div>
    <form @submit=${saveReminders}>
      <div class="card">
        <h3>${t('reminders.title', 'Automatic payment reminders')} <small>${t('reminders.small2', 'chase overdue invoices automatically')}</small></h3>
        <label class="fld inline"><span>${t('reminders.enable', 'Send automatic overdue reminders')}</span>
          <input type="checkbox" class="chk" .checked=${remEnabled} @change=${e => { remEnabled.value = e.target.checked; }}></label>
        ${dayTierField(remOff, remAddOff, t('reminders.offsets_cap', 'Days overdue'), t('reminders.add_ph', 'e.g. 30'))}
      </div>
      <div class="card">
        <h3>${t('reminders.quote_title', 'Quote follow-ups')} <small>${t('reminders.quote_small2', 'chase unanswered quotes automatically')}</small></h3>
        <label class="fld inline"><span>${t('reminders.quote_enable', 'Send automatic quote follow-ups')}</span>
          <input type="checkbox" class="chk" .checked=${remQuoteEnabled} @change=${e => { remQuoteEnabled.value = e.target.checked; }}></label>
        ${dayTierField(remQuoteOff, remAddQuote, t('reminders.quote_offsets_cap', 'Days after issue'), t('reminders.quote_add_ph', 'e.g. 5'))}
        ${() => remErr.value ? html`<div class="row-err">${remErr.value}</div>` : ''}
        <div class="form-actions">
          <button type="submit" class="btn primary" data-rem-save ?disabled=${() => remBusy.value}>${() => remBusy.value ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </div>
      </div>
    </form>
    <div class="card">
      <h3>${t('reminders.run_title', 'Send due reminders now')}</h3>
      <div class="form-actions">
        <button type="button" class="btn" data-rem-run ?disabled=${() => remRunning.value} @click=${sendDueReminders}>${() => remRunning.value ? t('reminders.sending', 'Sending…') : t('reminders.send_due', 'Send due reminders')}</button>
      </div>
      ${() => { const r = remRun.value; if(!r) return '';
        const inv = r.invoices || {}, quo = r.quotes || {}, sch = r.scheduled || {};
        const skips = [...(inv.skipped || []), ...(quo.skipped || []), ...(sch.skipped || [])];
        const noEmail = skips.filter(s => s.reason === 'no_email').length;
        return html`<div class="rem-run-summary">
          <div>${t('reminders.inv_sent', 'Invoice reminders sent')}: <b>${inv.count || 0}</b></div>
          <div>${t('reminders.quote_sent', 'Quote follow-ups sent')}: <b>${quo.count || 0}</b></div>
          <div>${t('reminders.sched_fired', 'Scheduled sends fired')}: <b>${sch.count || 0}</b></div>
          ${skips.length ? html`<div class="rem-run-skip">${t('reminders.skipped', 'Skipped')}: ${skips.length} (${noEmail} ${t('reminders.no_email', 'no billing email')})</div>` : ''}
          ${!(inv.enabled) ? html`<div class="rem-run-skip">${t('reminders.disabled_note', 'Automatic invoice reminders are switched off — enable them above first.')}</div>` : ''}
          ${!(quo.enabled) ? html`<div class="rem-run-skip">${t('reminders.quote_disabled_note', 'Quote follow-ups are switched off — enable them above first.')}</div>` : ''}
        </div>`; }}
    </div>
  </div>`;

  // ── tab: API Keys — the AI provider key card (multi-provider) + the developer/integration keys,
  // exactly as on staging (owner 2026-09-06) ──
  const companyMcpManager = () => html`<section class="company-mcp-keys" data-company-mcp-keys
      ?hidden=${() => !isCompanyAdmin(actorCompanyRole.value)}>
      <div class="set-section">
        <h3 class="set-section-title">MCP keys for this company</h3>
        <p class="set-section-note">This admin-scoped list shows masked keys tied to this company. New keys below are company-only; user-bound keys stay attached to their member and may access other companies.</p>
      </div>
      <form class="card" data-company-mcp-form @submit=${createCompanyMcpKey}>
        <h3>Create a company MCP key <small>Company-scoped assistant access</small></h3>
        <div class="grid2">
          <label class="fld"><span>${t('api_keys.name_label','Name')}</span>
            <input data-company-mcp-name .value=${cmName} @input=${e => { cmName.value = e.target.value; cmErr.value = ''; }} placeholder="e.g. Claude — reporting integration"></label>
          <label class="fld"><span>${t('api_keys.env_label','Environment')}</span>
            <select data-company-mcp-environment .value=${cmEnv} @change=${e => { cmEnv.value = e.target.value; }}>
              ${KEY_ENVS.map(([v, l, tk]) => html`<option value=${v}>${t(tk, l)}</option>`)}</select></label>
          <label class="fld"><span>Company role</span>
            <select data-company-mcp-level .value=${cmLevel} @change=${e => { cmLevel.value = e.target.value; }}>
              ${MCP_COMPANY_LEVELS.map(([v, l]) => html`<option value=${v}>${l}</option>`)}</select></label>
        </div>
        <div class="fld"><span>${t('mcp_keys.perms_label', 'Permissions')}</span>
          <div class="mcp-scopes">${MCP_SCOPES.map(([s, tk, lbl]) => html`
            <label class="mcp-scope"><input type="checkbox" data-company-mcp-scope=${s}
              .checked=${() => cmScopes.value.includes(s)} @change=${e => onCompanyMcpScopeChange(s, e)}>
              <span class="mcp-scope-code">${s}</span><span class="mcp-scope-lbl">${t(tk, lbl)}</span></label>`)}</div></div>
        <label class="fld inline"><span>Allow original source dates for quotes, invoices and credit notes</span>
          <input type="checkbox" data-company-mcp-source-dates .checked=${cmKeepsSourceDates}
            @change=${e => { cmKeepsSourceDates.value = e.target.checked === true; }}></label>
        <div class="hint">Off by default. When enabled, the assistant may preserve past source-system dates when creating supported documents.</div>
        ${() => cmErr.value ? html`<div class="row-err" data-company-mcp-create-error>${cmErr.value}</div>` : ''}
        <div class="form-actions"><button type="submit" class="btn primary" data-company-mcp-create ?disabled=${() => cmBusy.value}>
          ${() => cmBusy.value ? t('api_keys.creating', 'Creating…') : 'Create company MCP key'}</button></div>
      </form>
      ${() => cmSecret.value ? html`<div class="ak-secret" data-company-mcp-secret>
        <div class="ak-secret-title">${t('api_keys.secret_notice', '✓ Key created — copy it now. For security, you won\'t be able to see it again.')}</div>
        <div class="ak-secret-row"><code class="ak-secret-code">${cmSecret.value}</code>
          <button type="button" class="btn ak-copy" @click=${copyCompanyMcpSecret}>${t('api_keys.copy_button','Copy')}</button></div>
        ${(() => { const cfg = mcpClientConfig(cmSecret.value); return html`
          <div class="ak-secret-title mcp-setup-title">${t('mcp_keys.setup_cli', 'Claude Code — run this once:')}</div>
          <div class="ak-secret-row"><code class="ak-secret-code">${cfg.cli}</code><button type="button" class="btn ak-copy" @click=${() => copyText(cfg.cli)}>${t('api_keys.copy_button','Copy')}</button></div>
          <div class="ak-secret-title mcp-setup-title">${t('mcp_keys.setup_json', 'Claude Desktop, Cursor and other MCP apps — add to the MCP config:')}</div>
          <div class="ak-secret-row"><pre class="ak-secret-code mcp-json">${cfg.json}</pre><button type="button" class="btn ak-copy" @click=${() => copyText(cfg.json)}>${t('api_keys.copy_button','Copy')}</button></div>`; })()}
      </div>` : ''}
      <div class="muted-block" data-company-mcp-status>${() => cmKeysLoading.value
        ? 'Loading company MCP keys…'
        : cmKeysErr.value ? html`${cmKeysErr.value} <button type="button" class="btn" data-company-mcp-retry @click=${() => loadCompanyMcpKeys(true)}>Retry</button>`
          : ''}</div>
      <div class="tbl-wrap"><table class="list" data-company-mcp-list>
        <thead><tr><th>${t('api_keys.col_name','Name')}</th><th>${t('api_keys.col_key','Key')}</th><th>${t('api_keys.col_env','Env')}</th><th>Principal</th><th>Company role</th><th>${t('mcp_keys.col_perms','Permissions')}</th><th>Original dates</th><th>${t('api_keys.col_created','Created')}</th><th>${t('api_keys.col_status','Status')}</th><th></th></tr></thead>
        <tbody>${() => {
          const list = cmKeys.value, rid = cmRevokeId.value, busy = cmRevokeBusy.value;
          return list.length ? list.map(k => html`<tr class=${k.revoked ? 'ak-revoked' : ''} data-company-mcp-key=${k.id}>
            <td>${k.name}</td><td class="mono">${k.key_prefix}…</td><td><span class=${'ak-env ak-env-' + k.environment}>${k.environment}</span></td>
            <td>${k.principal_kind === 'company' ? 'Company-only' : k.principal_kind === 'user' ? 'User-bound' : 'Unknown'}</td>
            <td>${companyMcpRoleLabel(k)}</td><td class="muted">${(k.scopes || []).join(', ') || '—'}</td>
            <td>${k.keeps_source_dates ? 'Allowed' : 'Not allowed'}</td><td class="muted">${fmtDate(k.created_at)}</td>
            <td>${k.revoked ? html`<span class="ak-st-revoked">${t('api_keys.status_revoked','Revoked')}</span>` : html`<span class="ak-st-active">${t('api_keys.status_active','Active')}</span>`}</td>
            <td class="ta-r nowrap set-acts">${k.revoked ? '' : rowDelete({ armed: rid === k.id, busy, error: '',
              onArm: () => { cmRevokeId.value = k.id; }, onCancel: () => { cmRevokeId.value = null; },
              onDelete: () => revokeCompanyMcpKey(k.id), label: t('api_keys.revoke_button','Revoke') })}</td></tr>`)
            : cmKeysLoaded.value ? html`<tr><td colspan="10" class="muted">No MCP keys yet.</td></tr>` : '';
        }}</tbody>
      </table></div>
    </section>`;

  const apiKeysTab = () => html`<div class="set-apikeys">
    ${companyMcpManager()}
    <div class="set-section">
      <h3 class="set-section-title">${t('api_keys.ai_section_title', 'AI provider key')}
        ${() => aiDisabled.value
          ? html`<span class="set-badge set-badge-warn">${t('settings.ai_off_badge', 'Disabled')}</span>`
          : aiHasKey.value
          ? html`<span class="set-badge set-badge-ok">${t('settings.connected', 'Connected')}</span>`
          : aiServerKey.value
            ? html`<span class="set-badge set-badge-ok">${t('settings.ai_active', 'Active')}</span>`
            : html`<span class="set-badge set-badge-warn">${t('settings.not_configured', 'Not configured')}</span>`}</h3>
      <p class="set-section-note">${t('api_keys.ai_section_note', 'A key you get from an AI service — Gemini, OpenAI, Claude or Grok. Sleek uses it to read bank statements you drag in and to answer your questions. Stored against your user only and never shown again once saved. Optional.')}</p>
    </div>
    <form class="card set-card mb-2" @submit=${saveAI}><div class="card-body set-form">
      <div class="set-row"><label class="set-lbl">${t('settings.ai_provider', 'Provider')}</label>
        <select class="form-control" data-ai-provider .value=${aiProvider} @change=${e => aiPickProvider(e.target.value)}>
          ${() => Object.keys(aiProviders.value || {}).map(k => html`<option value=${k}>${(aiProviders.value[k] || {}).label || k}</option>`)}</select></div>
      <div class="set-row"><label class="set-lbl">${() => ((aiProv().label || t('settings.ai_provider', 'Provider')) + ' ' + t('settings.ai_api_key_word', 'API key'))}</label>
        <div class="set-key-row">
          <input class="form-control" type=${() => aiShowKey.value ? 'text' : 'password'} autocomplete="off" data-ai-key
            ?disabled=${() => aiDisabled.value || (!aiEditKey.value && (aiHasKey.value || aiServerKey.value))}
            .value=${aiKeyIn} @input=${e => { aiKeyIn.value = e.target.value; if(aiErr.value) aiErr.value = ''; }}
            placeholder=${() => aiDisabled.value ? t('settings.ai_off_ph', 'AI is off — press Enable to add a key')
              : aiEditKey.value ? (aiProv().key_hint || 'API key…')
              : aiHasKey.value ? (aiMasked.value + ' — ' + t('settings.ai_key_keep', 'leave blank to keep'))
              : aiServerKey.value ? ('•'.repeat(8) + ' — ' + t('settings.ai_key_active', 'AI key active'))
              : (aiProv().key_hint || 'API key…')}>
          ${() => aiDisabled.value
            ? html`<button type="button" class="btn btn-outline btn-sm set-key-act" ?disabled=${aiBusy.value} @click=${() => setAIDisabled(false)}>${t('settings.ai_enable', 'Enable AI')}</button>`
            : aiEditKey.value
              ? html`<button type="button" class="btn btn-outline btn-sm set-key-act" @click=${() => { aiShowKey.value = !aiShowKey.value; }}>${aiShowKey.value ? t('common.hide', 'Hide') : t('common.show', 'Show')}</button><button type="button" class="btn btn-outline btn-sm set-key-act" @click=${() => { aiEditKey.value = false; aiKeyIn.value = ''; aiShowKey.value = false; aiErr.value = ''; }}>${t('common.cancel', 'Cancel')}</button>`
              : (aiHasKey.value || aiServerKey.value)
                ? html`<button type="button" class="btn btn-outline btn-sm set-key-act" @click=${() => { aiEditKey.value = true; aiKeyIn.value = ''; }}>${t('settings.ai_change_key', 'Change key')}</button><button type="button" class="btn btn-outline btn-sm set-key-act" ?disabled=${aiBusy.value} @click=${() => setAIDisabled(true)}>${t('settings.ai_disable', 'Disable AI')}</button>`
                : html`<button type="button" class="btn btn-outline btn-sm set-key-act" @click=${() => { aiShowKey.value = !aiShowKey.value; }}>${aiShowKey.value ? t('common.hide', 'Hide') : t('common.show', 'Show')}</button>`}
        </div>
        <span class="hint">${() => aiProv().key_url ? html`${t('settings.get_key_at', 'Get a key at')} <a href=${aiProv().key_url} target="_blank" rel="noopener">${String(aiProv().key_url).replace(/^https?:\/\//, '')}</a>` : ''}</span></div>
      <div class="set-row"><label class="set-lbl">${t('settings.pdf_password', 'Bank-statement PDF password')}</label>
        <input class="form-control" type="password" autocomplete="off" data-ai-pdf .value=${aiPdfPw} @input=${e => { aiPdfPw.value = e.target.value; }} placeholder=${t('settings.pdf_password_ph', 'only if your statements are password-protected')}></div>
      <div class="set-row"><label class="set-lbl">${t('settings.ai_threshold_label', 'Auto-allocate confidence')} <span class="mono">${() => aiThreshPct.value + '%'}</span></label>
        <input type="range" class="set-range" min="50" max="100" step="5" data-ai-threshold .value=${aiThreshPct} @input=${e => { aiThreshPct.value = Number(e.target.value); }}>
        <span class="hint">${t('settings.ai_threshold_hint', 'How sure the AI must be before it auto-allocates an imported bank line. Higher = safer, fewer automatic matches.')}</span></div>
      <div class="set-row"><label class="set-lbl">${t('settings.ai_model_label', 'AI model')}</label>
        <select class="form-control" data-ai-model .value=${aiModel} @change=${e => { aiModel.value = e.target.value; }}>
          ${() => (aiProv().models || []).map(([v, l]) => html`<option value=${v}>${l}</option>`)}</select>
        <span class="hint">${t('settings.ai_model_hint', 'Model for statement extraction. Higher tiers cost more but can be more accurate.')}</span></div>
      ${() => aiErr.value ? html`<div class="row-err mt-2">${aiErr.value}</div>` : ''}
      <div class="set-actions">
        <button type="submit" class="btn btn-primary" data-ai-save ?disabled=${() => aiBusy.value}>${() => aiBusy.value ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        <button type="button" class="btn btn-outline" data-ai-test ?disabled=${() => aiBusy.value || aiTesting.value} @click=${testAI}>${() => aiTesting.value ? t('settings.testing', 'Testing…') : t('settings.test_key', 'Test key')}</button>
        <span class="set-note">${() => aiNote.value}</span>
      </div>
    </div></form>
    <div class="set-section">
      <h3 class="set-section-title">${t('api_keys.payfast_title', 'Online payments (PayFast)')}
        ${() => pfConfigured.value
          ? html`<span class="set-badge set-badge-ok">${t('settings.connected', 'Connected')}</span>`
          : html`<span class="set-badge set-badge-warn">${t('settings.not_configured', 'Not configured')}</span>`}</h3>
      <p class="set-section-note">${t('api_keys.payfast_note', 'Your own PayFast merchant account. Once set up, a customer can pay an invoice online from its share link and the money goes to YOUR PayFast account — not Sleek\'s. Until then, online payment stays hidden. The merchant key and passphrase are stored securely and never shown again.')}</p>
    </div>
    <form class="card set-card mb-2" @submit=${savePayfast} data-payfast-form><div class="card-body set-form">
      <div class="set-row"><label class="set-lbl">${t('settings.pf_merchant_id', 'Merchant ID')}</label>
        <input class="form-control" data-pf-merchant .value=${pfMerchantId} @input=${e => { pfMerchantId.value = e.target.value; if(pfErr.value) pfErr.value = ''; }} placeholder=${t('settings.pf_merchant_id_ph', 'e.g. 10000100')}></div>
      <div class="set-row"><label class="set-lbl">${t('settings.pf_merchant_key', 'Merchant key')}</label>
        <input class="form-control" type="password" autocomplete="off" data-pf-key .value=${pfKeyIn} @input=${e => { pfKeyIn.value = e.target.value; if(pfErr.value) pfErr.value = ''; }}
          placeholder=${() => pfHasKey.value ? t('settings.pf_key_keep', 'on file — leave blank to keep') : t('settings.pf_key_ph', 'from your PayFast dashboard')}></div>
      <div class="set-row"><label class="set-lbl">${t('settings.pf_passphrase', 'Passphrase')}</label>
        <input class="form-control" type="password" autocomplete="off" data-pf-pass .value=${pfPassIn} @input=${e => { pfPassIn.value = e.target.value; }}
          placeholder=${() => pfHasPass.value ? t('settings.pf_pass_keep', 'on file — leave blank to keep') : t('settings.pf_pass_ph', 'your PayFast security passphrase')}></div>
      <label class="fld inline set-row"><span class="set-lbl">${t('settings.pf_sandbox', 'Sandbox (test) mode')}</span>
        <input type="checkbox" class="chk" data-pf-sandbox .checked=${pfSandbox} @change=${e => { pfSandbox.value = e.target.checked; }}></label>
      ${() => pfErr.value ? html`<div class="row-err mt-2">${pfErr.value}</div>` : ''}
      <div class="set-actions">
        <button type="submit" class="btn btn-primary" data-pf-save ?disabled=${() => pfBusy.value}>${() => pfBusy.value ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        <span class="set-note">${() => pfNote.value}</span>
      </div>
    </div></form>
    <div class="set-section">
      <h3 class="set-section-title">${t('api_keys.dev_section_title', 'Developer & integration keys')}</h3>
      <p class="set-section-note">${t('api_keys.dev_section_note', "Keys Sleek issues to you so outside software — like Zapier or your own scripts — can reach your data through Sleek's API. Only needed if you're building an integration.")}</p>
    </div>
    <form class="card" @submit=${createKey}>
      <h3>${t('api_keys.create_title', 'Create an API key')} <small>${t('api_keys.small', 'programmatic access for integrations')}</small></h3>
      <div class="grid2">
        <label class="fld"><span>${t('api_keys.name_label','Name')}</span>
          <input .value=${kName} @input=${e => { if(kErr.value) kErr.value = ''; kName.value = e.target.value; }} placeholder=${t('api_keys.eg_name', 'e.g. Zapier integration')}></label>
        <label class="fld"><span>${t('api_keys.env_label','Environment')}</span>
          <select .value=${kEnv} @change=${e => { kEnv.value = e.target.value; }}>
            ${KEY_ENVS.map(([v, l, tk]) => html`<option value=${v}>${t(tk, l)}</option>`)}</select></label>
        <label class="fld"><span>${t('api_keys.scopes_label', 'Scopes (comma-separated, optional)')}</span>
          <input .value=${kScopes} @input=${e => { kScopes.value = e.target.value; }} placeholder=${t('api_keys.eg_scopes', 'e.g. read:ledger, write:invoicing')}></label>
      </div>
      ${() => kErr.value ? html`<div class="row-err">${kErr}</div>` : ''}
      <div class="hint">${t('api_keys.usage_hint', 'A key authenticates API calls for THIS company only: send it as an X-API-Key header (or as the bearer itself) — e.g. curl -H "X-API-Key: sk_live_…" …/api/companies/{id}/dashboard. It acts as the user who created it, and revoking it here cuts access immediately.')}</div>
      <div class="form-actions">
        <button type="submit" class="btn primary" ?disabled=${() => kBusy.value}>${() => kBusy.value ? t('api_keys.creating', 'Creating…') : t('api_keys.create_key', 'Create key')}</button>
      </div>
    </form>
    ${() => newSecret.value ? html`<div class="ak-secret">
      <div class="ak-secret-title">${t('api_keys.secret_notice', '✓ Key created — copy it now. For security, you won\'t be able to see it again.')}</div>
      <div class="ak-secret-row"><code class="ak-secret-code">${newSecret.value}</code>
        <button type="button" class="btn ak-copy" @click=${copySecret}>${t('api_keys.copy_button','Copy')}</button></div>
    </div>` : ''}
    <div class="tbl-wrap"><table class="list">
      <thead><tr><th>${t('api_keys.col_name','Name')}</th><th>${t('api_keys.col_key','Key')}</th><th>${t('api_keys.col_env','Env')}</th><th>${t('api_keys.col_scopes','Scopes')}</th><th>${t('api_keys.col_created','Created')}</th><th>${t('api_keys.col_status','Status')}</th><th></th></tr></thead>
      <tbody>${() => {
        const list = keyList.value.filter(k => k.kind !== 'mcp'), rid = revokeId.value, rbusy = revokeBusy.value;
        return list.length ? list.map(k => html`<tr class=${k.revoked ? 'ak-revoked' : ''}>
          <td>${k.name}</td>
          <td class="mono">${k.key_prefix}…</td>
          <td><span class=${'ak-env ak-env-' + k.environment}>${k.environment}</span></td>
          <td class="muted">${(k.scopes || []).join(', ') || '—'}</td>
          <td class="muted">${fmtDate(k.created_at)}</td>
          <td>${k.revoked ? html`<span class="ak-st-revoked">${t('api_keys.status_revoked','Revoked')}</span>` : html`<span class="ak-st-active">${t('api_keys.status_active','Active')}</span>`}</td>
          <td class="ta-r nowrap set-acts">${k.revoked ? '' : rowDelete({ armed: rid === k.id, busy: rbusy, error: '',
            onArm: () => { revokeId.value = k.id; }, onCancel: () => { revokeId.value = null; },
            onDelete: () => revokeKey(k.id), label: t('api_keys.revoke_button','Revoke') })}</td></tr>`)
          : html`<tr><td colspan="7" class="muted">${t('api_keys.none_yet', 'No API keys yet.')}</td></tr>`;
      }}</tbody>
    </table></div>

    <div class="set-section">
      <h3 class="set-section-title">${t('mcp_keys.section_title', 'MCP keys (AI access)')}</h3>
      <p class="set-section-note">${t('mcp_keys.section_note', "Keys that let an AI assistant work in your books over MCP — make out any document, reconcile banking and pull reports. A key acts as you and reaches every company you can access; tick exactly what it may do. Connect it at POST /api/mcp with an Authorization: Bearer sk_… header.")}</p>
    </div>
    <div class="card mcp-connect">
      <h3>${t('mcp_keys.connect_title', 'Connect an AI assistant')}</h3>
      <p class="muted">${t('mcp_keys.connect_note', 'Add aboo to Claude, Cursor or any MCP app as a remote server with this URL, and send your MCP key as the header Authorization: Bearer <key>.')}</p>
      <div class="ak-secret-row"><code class="ak-secret-code mcp-url">${mcpUrl()}</code>
        <button type="button" class="btn ak-copy" @click=${() => copyText(mcpUrl())}>${t('mcp_keys.copy_url', 'Copy URL')}</button></div>
    </div>
    <form class="card" @submit=${createMcpKey}>
      <h3>${t('mcp_keys.create_title', 'Create an MCP key')} <small>${t('mcp_keys.small', 'AI access to your accounting')}</small></h3>
      <div class="grid2">
        <label class="fld"><span>${t('api_keys.name_label','Name')}</span>
          <input .value=${mName} @input=${e => { if(mErr.value) mErr.value = ''; mName.value = e.target.value; }} placeholder=${t('mcp_keys.eg_name', 'e.g. Claude — my accountant')}></label>
        <label class="fld"><span>${t('api_keys.env_label','Environment')}</span>
          <select .value=${mEnv} @change=${e => { mEnv.value = e.target.value; }}>
            ${KEY_ENVS.map(([v, l, tk]) => html`<option value=${v}>${t(tk, l)}</option>`)}</select></label>
      </div>
      <div class="fld"><span>${t('mcp_keys.perms_label', 'Permissions')}</span>
        <div class="mcp-scopes">${() => { const sel = mScopes.value; return MCP_SCOPES.map(([s, tk, lbl]) => html`
          <label class="mcp-scope"><input type="checkbox" ?checked=${sel.indexOf(s) >= 0} @change=${() => toggleMcpScope(s)}>
            <span class="mcp-scope-code">${s}</span><span class="mcp-scope-lbl">${t(tk, lbl)}</span></label>`); }}</div></div>
      ${() => mErr.value ? html`<div class="row-err">${mErr}</div>` : ''}
      <div class="form-actions">
        <button type="submit" class="btn primary" ?disabled=${() => mBusy.value}>${() => mBusy.value ? t('api_keys.creating', 'Creating…') : t('mcp_keys.create_key', 'Create MCP key')}</button>
      </div>
    </form>
    ${() => mSecret.value ? html`<div class="ak-secret">
      <div class="ak-secret-title">${t('api_keys.secret_notice', '✓ Key created — copy it now. For security, you won\'t be able to see it again.')}</div>
      <div class="ak-secret-row"><code class="ak-secret-code">${mSecret.value}</code>
        <button type="button" class="btn ak-copy" @click=${copyMcpSecret}>${t('api_keys.copy_button','Copy')}</button></div>
      ${(() => { const cfg = mcpClientConfig(mSecret.value); return html`
      <div class="ak-secret-title mcp-setup-title">${t('mcp_keys.setup_cli', 'Claude Code — run this once:')}</div>
      <div class="ak-secret-row"><code class="ak-secret-code">${cfg.cli}</code>
        <button type="button" class="btn ak-copy" @click=${() => copyText(cfg.cli)}>${t('api_keys.copy_button','Copy')}</button></div>
      <div class="ak-secret-title mcp-setup-title">${t('mcp_keys.setup_json', 'Claude Desktop, Cursor and other MCP apps — add to the MCP config:')}</div>
      <div class="ak-secret-row"><pre class="ak-secret-code mcp-json">${cfg.json}</pre>
        <button type="button" class="btn ak-copy" @click=${() => copyText(cfg.json)}>${t('api_keys.copy_button','Copy')}</button></div>`; })()}
    </div>` : ''}
    <div class="tbl-wrap"><table class="list">
      <thead><tr><th>${t('api_keys.col_name','Name')}</th><th>${t('api_keys.col_key','Key')}</th><th>${t('api_keys.col_env','Env')}</th><th>${t('mcp_keys.col_perms','Permissions')}</th><th>${t('api_keys.col_created','Created')}</th><th>${t('api_keys.col_status','Status')}</th><th></th></tr></thead>
      <tbody>${() => {
        const list = keyList.value.filter(k => k.kind === 'mcp'), rid = revokeId.value, rbusy = revokeBusy.value;
        return list.length ? list.map(k => html`<tr class=${k.revoked ? 'ak-revoked' : ''}>
          <td>${k.name}</td>
          <td class="mono">${k.key_prefix}…</td>
          <td><span class=${'ak-env ak-env-' + k.environment}>${k.environment}</span></td>
          <td class="muted">${(k.scopes || []).join(', ') || '—'}</td>
          <td class="muted">${fmtDate(k.created_at)}</td>
          <td>${k.revoked ? html`<span class="ak-st-revoked">${t('api_keys.status_revoked','Revoked')}</span>` : html`<span class="ak-st-active">${t('api_keys.status_active','Active')}</span>`}</td>
          <td class="ta-r nowrap set-acts">${k.revoked ? '' : rowDelete({ armed: rid === k.id, busy: rbusy, error: '',
            onArm: () => { revokeId.value = k.id; }, onCancel: () => { revokeId.value = null; },
            onDelete: () => revokeKey(k.id), label: t('api_keys.revoke_button','Revoke') })}</td></tr>`)
          : html`<tr><td colspan="7" class="muted">${t('mcp_keys.none_yet', 'No MCP keys yet.')}</td></tr>`;
      }}</tbody>
    </table></div>
  </div>`;

  // ── tab: Communications ──
  const templateForm = () => html`<form class="card" @submit=${saveT}>
    <h3>${() => tEditId.value ? t('comms.edit_template', 'Edit template') : t('comms.new_template', 'New template')}</h3>
    <div class="grid2">
      <label class="fld"><span>${t('common.code','Code')}</span>
        <input .value=${tCode} @input=${e => { tCode.value = e.target.value; if(tErr.value) tErr.value = ''; }} placeholder=${t('comms.eg_code', 'e.g. INV-EMAIL')} ?disabled=${() => !!tEditId.value}></label>
      <label class="fld"><span>${t('comms.kind', 'Kind')}</span>
        <select .value=${tKind} @change=${e => { tKind.value = e.target.value; }}>
          ${COMM_KINDS.map(k => html`<option value=${k}>${k}</option>`)}</select></label>
      <label class="fld"><span>${t('common.name','Name')}</span>
        <input .value=${tName} @input=${e => { tName.value = e.target.value; if(tErr.value) tErr.value = ''; }} placeholder=${t('comms.eg_name', 'e.g. Invoice email to customer')}></label>
      <label class="fld"><span>${t('comms.subject', 'Subject')}</span>
        <input .value=${tSubject} @input=${e => { tSubject.value = e.target.value; if(tErr.value) tErr.value = ''; }} placeholder=${t('comms.eg_subject', 'e.g. Your invoice [DOCNO] from [COMPANY]')}></label>
    </div>
    <label class="fld"><span>${t('comms.body', 'Body')}</span>
      <textarea class="bt-area cm-body" rows="5" .value=${tBody} @input=${e => { tBody.value = e.target.value; if(tErr.value) tErr.value = ''; }} placeholder=${t('comms.eg_body', 'e.g. Hi [CONTACT], please find invoice [DOCNO] attached. Total due: [AMOUNT]. Thank you.')}></textarea></label>
    <div class="hint">${t('comms.tokens_hint', 'Tokens: [CONTACT] [COMPANY] [DOCNO] [AMOUNT] [DATE] [MONTH] [YEAR] — substituted when the email is sent.')}</div>
    <label class="fld inline"><span>${t('comms.active', 'Active')}</span>
      <input type="checkbox" class="chk" .checked=${tActive} @change=${e => { tActive.value = e.target.checked; }}></label>
    ${() => tErr.value ? html`<div class="row-err">${tErr}</div>` : ''}
    <div class="form-actions">
      <button type="submit" class="btn primary" ?disabled=${() => tBusy.value}>${() => tBusy.value ? t('common.saving', 'Saving…') : (tEditId.value ? t('comms.save_template', 'Save template') : t('comms.create_template', 'Create template'))}</button>
      <button type="button" class="btn" @click=${closeT}>${t('common.cancel','Cancel')}</button>
    </div>
  </form>`;

  const templatesInner = () => html`<div>
    <div class="list-toolbar set-coa-bar"><span class="jrn-spacer"></span>
      <button class="btn primary" @click=${openNewT}>${t('comms.new_template_btn', '+ New template')}</button></div>
    ${() => tOpen.value ? templateForm() : ''}
    <div class="tbl-wrap"><table class="list">
      <thead><tr><th>${t('common.code','Code')}</th><th>${t('common.name','Name')}</th><th>${t('comms.kind', 'Kind')}</th><th>${t('comms.subject', 'Subject')}</th><th>${t('comms.active', 'Active')}</th><th></th></tr></thead>
      <tbody>${() => {
        const list = templates.value, del = tDelId.value, dbusy = tDelBusy.value, derr = tDelErr.value;
        const editLbl = t('common.edit', 'Edit'), sysLbl = t('comms.system_tag', '· system'), roLbl = t('comms.read_only', 'read-only');
        return list.length ? list.map(tpl => html`<tr class=${tpl.company_id ? '' : 'cm-sys'}>
          <td class="mono">${tpl.code}</td>
          <td>${tpl.name}${tpl.company_id ? '' : html` <span class="muted">${sysLbl}</span>`}</td>
          <td><span class="cm-kind">${tpl.kind}</span></td>
          <td class="muted cm-subj">${tpl.subject}</td>
          <td>${tpl.is_active ? '✓' : ''}</td>
          <td class="ta-r nowrap set-acts">${tpl.company_id ? html`<button class="icon-btn" title=${editLbl} @click=${() => openEditT(tpl)}>✎</button>${rowDelete({ armed: del === tpl.id, busy: dbusy, error: del === tpl.id ? derr : '', onArm: () => { tDelErr.value = ''; tDelId.value = tpl.id; }, onCancel: () => { tDelId.value = null; }, onDelete: () => doDeleteT(tpl.id) })}` : html`<span class="muted">${roLbl}</span>`}</td></tr>`)
          : html`<tr><td colspan="6" class="muted">${t('comms.no_templates', 'No templates yet — add one to standardise your invoice & statement emails.')}</td></tr>`;
      }}</tbody>
    </table></div>
  </div>`;

  const logInner = () => html`<div>
    <div class="muted-block set-vat-note">${t('comms.log_note', 'Every email your company sends (invoices, statements, reminders) is logged here with delivery status. While no mail server is configured, messages stay “queued”.')}</div>
    <div class="tbl-wrap"><table class="list">
      <thead><tr><th>${t('comms.col_queued', 'Queued')}</th><th>${t('common.to','To')}</th><th>${t('comms.subject', 'Subject')}</th><th>${t('comms.kind', 'Kind')}</th><th>${t('common.status','Status')}</th></tr></thead>
      <tbody>${() => {
        const list = messages.value;
        return list.length ? list.map(m => html`<tr>
          <td class="muted">${fmtDate(m.queued_at)}</td>
          <td class="mono">${m.to_address}</td>
          <td>${m.subject}</td>
          <td><span class="cm-kind">${m.kind}</span></td>
          <td><span class=${'cm-st cm-st-' + m.status}>${m.status}</span></td></tr>`)
          : html`<tr><td colspan="5" class="muted">${t('comms.no_messages', 'No messages sent yet.')}</td></tr>`;
      }}</tbody>
    </table></div>
  </div>`;

  const commTab = () => html`<div>
    <div class="segs subtabs cm-subtabs">
      <button class=${() => 'seg' + (commSub.value === 'templates' ? ' on' : '')} @click=${() => { if(commSub.value !== 'templates'){ commSub.value = 'templates'; pane(); } }}>${() => t('comms.tab_templates', 'Email templates')}</button>
      <button class=${() => 'seg' + (commSub.value === 'log' ? ' on' : '')} @click=${() => { if(commSub.value !== 'log'){ commSub.value = 'log'; pane(); } }}>${() => t('comms.tab_log', 'Sent log')}</button>
    </div>
    <div class=${() => (commSub.value === 'templates' ? '' : 'inv-hidden') + (pane.on.value ? ' pane-in' : '')}>${templatesInner()}</div>
    <div class=${() => (commSub.value === 'log' ? '' : 'inv-hidden') + (pane.on.value ? ' pane-in' : '')}>${logInner()}</div>
  </div>`;

  const view = () => {
    locale.value;   // depend on the active locale so the tab content re-renders on a language flip
    if(loading.value) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    if(err.value) return html`<div class="muted-block">${err.value}</div>`;
    const tv = tab.value;
    // Fall the deep-linked-but-hidden Forex tab back to Company when the toggle is off.
    if(tv === 'forex' && !cMulti.value){ tab.value = 'company'; return companyTab(); }
    return tv === 'forex' ? forexTab()
      : tv === 'company' ? companyTab() : tv === 'coa' ? coaTab()
      : tv === 'branding' ? html`<div>${brandingTab()}<div class="set-comms-block">${commTab()}</div>${remindersTab()}</div>`
      : tv === 'apikeys' ? apiKeysTab() : '';
  };

  return tenantShell(companyId, t('common.settings', 'Settings'), html`<div class="page page-wide">
    <div class="page-head">
      <div><span class="eyebrow">${() => t('settings.eyebrow', 'Setup')}</span><h1>${() => t('common.settings','Settings')}</h1></div>
    </div>
    <div class="segs subtabs set-tabs">${() => visibleTabs().map(([k, lbl, tk]) => html`
      <button class=${() => 'seg' + (tab.value === k ? ' on' : '')} @click=${() => selectTab(k)}>${() => t(tk, lbl)}</button>`)}</div>
    <div class=${pane.cls('')}>${view}</div>
  </div>`);
}
